import { ReparoApi } from '@api';
import { fetchSalesInsights } from '@api/sales';
import PageHeader from '@components/common/PageHeader';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import { GenericChartCard } from '@components/ui/Chart';
import {
    DataSheet,
    DataSheetPagination,
    type DataSheetColumn,
    type DataSheetFilterConfig,
    type DataSheetTab,
} from '@components/ui/DataSheet';
import EntityFilterDropdown, {
    type FilterValue,
} from '@components/ui/EntityFilterDropdown';
import FilterDropdown, { type DropdownOption } from '@components/ui/Dropdown';
import KpiCard from '@components/ui/KpiCard';
import SegmentedToggle from '@components/ui/SegmentedToggle';
import { DatePopover, DateRangePicker } from '@components/ui/DateRange';
import usePersistentState from '@hooks/usePersistentState';
import {
    toIsActive,
    useProductFilterParams,
} from '@hooks/useProductFilterParams';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import { getMatchingEntityIds } from '@api/entity-attributes';
import { resolveAttributeFilterEntityIds } from '@hooks/useEntityAttributes';
import {
    ActionIcon,
    Checkbox,
    Popover,
    Stack as MStack,
    Text as MText,
    Tooltip,
} from '@mantine/core';
import {
    Columns,
    Export,
    XCircle,
    DownloadSimple,
} from '@phosphor-icons/react';
import {
    endOfISOWeek,
    endOfMonth,
    format,
    parseISO,
    startOfISOWeek,
    startOfMonth,
    subMonths,
    subWeeks,
} from 'date-fns';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router';

import {
    buildPreviousRange,
    computeGrowthLabel,
    defaultLast4Weeks,
    formatDisplayRange,
    getPeriodKeyMeta,
    normalizeDateKey,
} from '../demand/dateUtils';
import type { PeriodType } from '../demand/types';
import { buildInsightsContext } from './buildInsightsContext';
import SalesInsightsCard from './SalesInsightsCard';
import type { ProductRelationEdge } from '@api/products';
import { useBundleReporting, type BundleView } from '@hooks/useBundleReporting';
import { loadRelations } from '@hooks/useSkuExplosion';
import {
    attachBundleBreakdown,
    buildAsSoldRows,
    buildBundleRecipes,
    hasBundleSales,
    toBundleParts,
} from './bundleReporting';
import {
    allocatedRevenueHeader,
    buildAsSoldColumns,
    buildBundleShareColumns,
} from './bundleColumns';
import type {
    ChannelRow,
    DateRange,
    GroupByOption,
    RevenueMode,
    SalesAggregateResponse,
    SalesAggregateRow,
    SalesChartPoint,
    SalesInsightItem,
    SkuRow,
    AsSoldRow,
    TrendResponse,
} from './types';
import { formatCount } from '@utils/currency';

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                    */
/* -------------------------------------------------------------------------- */

type ChannelOptionResponse = {
    channelId?: string;
    name?: string;
    abbreviationKey?: string;
    type?: string;
    model?: string;
};

const GROUP_BY_OPTIONS: DropdownOption[] = [
    { label: 'Product', value: 'product' },
    { label: 'Category', value: 'category' },
    { label: 'Product Group', value: 'productGroup' },
    { label: 'Tier', value: 'tier' },
];

type PeriodDateRange = { startDate: string; endDate: string };

/* Preset counts are inclusive of the current period: "Last 3 Months" really does
 * span three calendar months. The ranges they produce are already aligned to
 * month / ISO-week boundaries, so they can be written straight into a card's
 * date range without going through snapRangeToPeriod. */
const MONTHLY_PRESETS = [
    { value: 'Current Month', label: 'Current Month', months: 1 },
    { value: 'Last 3 Months', label: 'Last 3 Months', months: 3 },
    { value: 'Last 6 Months', label: 'Last 6 Months', months: 6 },
    { value: 'Last 12 Months', label: 'Last 12 Months', months: 12 },
];

const WEEKLY_PRESETS = [
    { value: 'Current Week', label: 'Current Week', weeks: 1 },
    { value: 'Last 4 Weeks', label: 'Last 4 Weeks', weeks: 4 },
    { value: 'Last 8 Weeks', label: 'Last 8 Weeks', weeks: 8 },
    { value: 'Last 12 Weeks', label: 'Last 12 Weeks', weeks: 12 },
];

function computeMonthlyRange(monthCount: number): PeriodDateRange {
    const today = new Date();
    const start = subMonths(today, Math.max(monthCount, 1) - 1);
    return {
        startDate: format(startOfMonth(start), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
    };
}

function computeWeeklyRange(weekCount: number): PeriodDateRange {
    const today = new Date();
    const start = subWeeks(today, Math.max(weekCount, 1) - 1);
    return {
        startDate: format(startOfISOWeek(start), 'yyyy-MM-dd'),
        endDate: format(endOfISOWeek(today), 'yyyy-MM-dd'),
    };
}

/**
 * Widen a range out to whole months / ISO weeks. Period columns bucket the card's
 * own date range, so an unsnapped range would render a partial first or last
 * bucket under a full-looking header (Jul 07-31 labelled "Jul 2026").
 */
function snapRangeToPeriod(
    range: PeriodDateRange,
    period: 'monthly' | 'weekly',
): PeriodDateRange {
    const start = parseISO(range.startDate);
    const end = parseISO(range.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return range;
    const [from, to] =
        period === 'monthly'
            ? [startOfMonth(start), endOfMonth(end)]
            : [startOfISOWeek(start), endOfISOWeek(end)];
    return {
        startDate: format(from, 'yyyy-MM-dd'),
        endDate: format(to, 'yyyy-MM-dd'),
    };
}

function matchMonthlyPreset(range: PeriodDateRange): string {
    for (const p of MONTHLY_PRESETS) {
        const r = computeMonthlyRange(p.months);
        if (r.startDate === range.startDate && r.endDate === range.endDate)
            return p.value;
    }
    return 'Custom';
}

function matchWeeklyPreset(range: PeriodDateRange): string {
    for (const p of WEEKLY_PRESETS) {
        const r = computeWeeklyRange(p.weeks);
        if (r.startDate === range.startDate && r.endDate === range.endDate)
            return p.value;
    }
    return 'Custom';
}

function trendDirection(label: string): 'up' | 'down' | 'neutral' {
    if (label.startsWith('+')) return 'up';
    if (label.startsWith('-')) return 'down';
    return 'neutral';
}

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

function formatCurrency(n: number): string {
    if (n >= 10_000_000) return `\u20B9${(n / 10_000_000).toFixed(2)}Cr`;
    if (n >= 100_000) return `\u20B9${(n / 100_000).toFixed(2)}L`;
    if (n >= 1_000) return `\u20B9${(n / 1_000).toFixed(1)}K`;
    return `\u20B9${formatCount(n)}`;
}

/** Match rows from two periods by a key function, return current + previous quantity and revenue. */
function matchGrowth<T extends { data: { quantity: number; revenue: number } }>(
    current: T[],
    previous: T[],
    keyFn: (row: T) => string,
): Map<
    string,
    {
        current: number;
        previous: number;
        currentRevenue: number;
        previousRevenue: number;
    }
> {
    const map = new Map<
        string,
        {
            current: number;
            previous: number;
            currentRevenue: number;
            previousRevenue: number;
        }
    >();
    for (const row of current) {
        const k = keyFn(row);
        const entry = map.get(k) ?? {
            current: 0,
            previous: 0,
            currentRevenue: 0,
            previousRevenue: 0,
        };
        entry.current += row.data.quantity || 0;
        entry.currentRevenue += row.data.revenue || 0;
        map.set(k, entry);
    }
    for (const row of previous) {
        const k = keyFn(row);
        const entry = map.get(k) ?? {
            current: 0,
            previous: 0,
            currentRevenue: 0,
            previousRevenue: 0,
        };
        entry.previous += row.data.quantity || 0;
        entry.previousRevenue += row.data.revenue || 0;
        map.set(k, entry);
    }
    return map;
}

/* -------------------------------------------------------------------------- */
/*                             Column definitions                              */
/* -------------------------------------------------------------------------- */

const revenueColumnHeader = (mode: RevenueMode): string =>
    mode === 'catalog' ? 'Revenue (Catalog)' : 'Revenue';

/* Columns the user may hide via the picker. The identity column (Channel / SKU)
 * and the gross Units column are intentionally NOT listed -> always shown. */
const CHANNEL_HIDEABLE_COLUMNS: { key: string; label: string }[] = [
    { key: 'returnQuantity', label: 'Return Units' },
    { key: 'netQuantity', label: 'Net Units' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'returnValue', label: 'Return Value' },
    { key: 'netValue', label: 'Net Value' },
    { key: 'orders', label: 'Orders' },
    { key: 'share', label: 'Share' },
    { key: 'growth', label: 'Growth' },
];

const SKU_HIDEABLE_COLUMNS: { key: string; label: string }[] = [
    { key: 'returnQuantity', label: 'Return Units' },
    { key: 'netQuantity', label: 'Net Units' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'returnValue', label: 'Return Value' },
    { key: 'netValue', label: 'Net Value' },
    { key: 'asp', label: 'ASP' },
    { key: 'mrp', label: 'MRP' },
    { key: 'growth', label: 'Growth' },
];

const ColumnPicker = ({
    columns,
    hidden,
    onChange,
}: {
    columns: { key: string; label: string }[];
    hidden: string[];
    onChange: (next: string[]) => void;
}) => {
    const hiddenSet = new Set(hidden);
    const toggle = (key: string, checked: boolean): void => {
        onChange(checked ? hidden.filter((k) => k !== key) : [...hidden, key]);
    };
    return (
        <Popover position="bottom-end" withArrow shadow="md">
            <Popover.Target>
                <Tooltip label="Columns" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                        <Columns size={16} />
                    </ActionIcon>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
                <MStack gap="xs">
                    <MText size="xs" fw={600} c="dimmed">
                        Show / Hide Columns
                    </MText>
                    {columns.map((col) => (
                        <Checkbox
                            key={col.key}
                            label={col.label}
                            size="xs"
                            color="violet"
                            checked={!hiddenSet.has(col.key)}
                            onChange={(e) =>
                                toggle(col.key, e.currentTarget.checked)
                            }
                        />
                    ))}
                </MStack>
            </Popover.Dropdown>
        </Popover>
    );
};

type TrendPeriod = 'summary' | 'monthly' | 'weekly';

/**
 * Debounce delay for a fetch effect: none on the first run, 350ms after.
 *
 * The debounce exists to coalesce bursts while a filter or date is being
 * changed. On mount there is no earlier request to coalesce with, so the delay
 * was pure dead time before the page asked for its first byte.
 */
const FILTER_DEBOUNCE_MS = 350;
const debounceFor = (firstRun: React.MutableRefObject<boolean>): number => {
    if (!firstRun.current) return FILTER_DEBOUNCE_MS;
    firstRun.current = false;
    return 0;
};

/** Sheets of the Performance card. SKU leads — it is what operators reach for. */
type CardTab = 'sku' | 'channel';

const CARD_TABS: DataSheetTab[] = [
    { id: 'sku', label: 'SKU Performance' },
    { id: 'channel', label: 'Channel Performance' },
];

/**
 * A card's date range + Summary/Monthly/Weekly toggle + period presets. One
 * range drives all three views: Monthly and Weekly just re-bucket it, so the
 * rows and the period columns are always scoped identically.
 */
const PeriodControls = ({
    period,
    onPeriodChange,
    range,
    onRangeChange,
    onRangeReset,
}: {
    period: TrendPeriod;
    onPeriodChange: (next: TrendPeriod) => void;
    range: PeriodDateRange;
    onRangeChange: (next: PeriodDateRange) => void;
    /** Only passed once the card range has been detached from the page range. */
    onRangeReset?: () => void;
}) => {
    const isMonthly = period === 'monthly';
    const presets = isMonthly ? MONTHLY_PRESETS : WEEKLY_PRESETS;

    return (
        <>
            <DateRangePicker
                value={range}
                onChange={(next) =>
                    onRangeChange(
                        period === 'summary'
                            ? next
                            : snapRangeToPeriod(next, period),
                    )
                }
                onReset={onRangeReset}
                title={
                    onRangeReset
                        ? 'This card has its own date range'
                        : 'Following the page date range'
                }
            />
            <SegmentedToggle
                value={period}
                onChange={(val) => {
                    const next = val as TrendPeriod;
                    onPeriodChange(next);
                    // Widen to whole months / weeks so no column is a partial bucket.
                    if (next !== 'summary')
                        onRangeChange(snapRangeToPeriod(range, next));
                }}
                options={[
                    { label: 'Summary', value: 'summary' },
                    { label: 'Monthly', value: 'monthly' },
                    { label: 'Weekly', value: 'weekly' },
                ]}
            />
            {period !== 'summary' && (
                <FilterDropdown
                    value={
                        isMonthly
                            ? matchMonthlyPreset(range)
                            : matchWeeklyPreset(range)
                    }
                    onChange={(val) => {
                        const preset = presets.find((p) => p.value === val);
                        if (!preset) return;
                        onRangeChange(
                            isMonthly
                                ? computeMonthlyRange(
                                      (preset as { months: number }).months,
                                  )
                                : computeWeeklyRange(
                                      (preset as { weeks: number }).weeks,
                                  ),
                        );
                    }}
                    options={presets.map((p) => ({
                        value: p.value,
                        label: p.label,
                    }))}
                    placeholder="Period"
                />
            )}
        </>
    );
};

const buildChannelColumns = (
    revenueMode: RevenueMode,
): DataSheetColumn<ChannelRow>[] => [
    {
        key: 'channelId',
        header: 'Channel',
        width: 160,
        sticky: true,
        render: (value: string) => (
            <span style={{ fontWeight: 600, color: 'rgb(26, 32, 44)' }}>
                {value}
            </span>
        ),
    },
    {
        key: 'units',
        header: 'Units',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {formatCount(value) ?? '—'}
            </span>
        ),
    },
    {
        key: 'returnQuantity',
        header: 'Return Units',
        width: 120,
        align: 'right',
        sortable: true,
        render: (value: number | null | undefined) => (
            <span
                style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'rgb(100, 116, 139)',
                }}
            >
                {value == null ? '—' : formatCount(value)}
            </span>
        ),
    },
    {
        key: 'netQuantity',
        header: 'Net Units',
        width: 110,
        align: 'right',
        sortable: true,
        render: (value: number | null | undefined) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {value == null ? '—' : formatCount(value)}
            </span>
        ),
    },
    {
        key: 'revenue',
        header: revenueColumnHeader(revenueMode),
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'returnValue',
        header: 'Return Value',
        width: 120,
        align: 'right',
        sortable: true,
        render: (value: number | null | undefined) => (
            <span
                style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'rgb(100, 116, 139)',
                }}
            >
                {value == null ? '—' : formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'netValue',
        header: 'Net Value',
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number | null | undefined) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {value == null ? '—' : formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'orders',
        header: 'Orders',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(value) ?? '—'}
            </span>
        ),
    },
    {
        key: 'share',
        header: 'Share',
        width: 90,
        align: 'right',
        render: (value: number) => `${value.toFixed(1)}%`,
    },
    {
        key: 'growth',
        header: 'Growth',
        width: 110,
        align: 'right',
        render: (value: string) => {
            const dir = trendDirection(value);
            const color =
                dir === 'up'
                    ? 'rgb(5, 150, 105)'
                    : dir === 'down'
                      ? 'rgb(220, 38, 38)'
                      : 'rgb(100, 116, 139)';
            return <span style={{ color, fontWeight: 500 }}>{value}</span>;
        },
    },
];

const buildSkuColumns = (
    rankOffset = 0,
    revenueMode: RevenueMode = 'reported',
): DataSheetColumn<SkuRow>[] => [
    {
        key: 'rank',
        header: '#',
        width: 50,
        align: 'center',
        render: (_v: unknown, row: SkuRow, idx: number) =>
            row.isBundleChild ? null : (
                <span style={{ color: 'rgb(100, 116, 139)', fontWeight: 500 }}>
                    {rankOffset + idx + 1}
                </span>
            ),
    },
    {
        key: 'name',
        header: 'SKU',
        width: 260,
        sticky: true,
        render: (_v: unknown, row: SkuRow) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span
                    style={{
                        fontWeight: 600,
                        color: 'rgb(26, 32, 44)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        // Bundle names are the point of a child row: wrap, never cut.
                        whiteSpace: row.isBundleChild ? 'normal' : 'nowrap',
                    }}
                >
                    {row.name || '—'}
                </span>
                <span
                    style={{
                        fontSize: 11,
                        color: 'rgb(100, 116, 139)',
                        fontFamily: 'monospace',
                    }}
                >
                    {row.isBundleChild ? row.sku : row.sku || '—'}
                </span>
            </div>
        ),
    },
    {
        key: 'units',
        header: 'Units',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {formatCount(value) ?? '—'}
            </span>
        ),
    },
    {
        key: 'returnQuantity',
        header: 'Return Units',
        width: 120,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span
                style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'rgb(100, 116, 139)',
                }}
            >
                {value == null ? '—' : formatCount(value)}
            </span>
        ),
    },
    {
        key: 'netQuantity',
        header: 'Net Units',
        width: 110,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {value == null ? '—' : formatCount(value)}
            </span>
        ),
    },
    {
        key: 'revenue',
        header: revenueColumnHeader(revenueMode),
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'returnValue',
        header: 'Return Value',
        width: 120,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span
                style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'rgb(100, 116, 139)',
                }}
            >
                {value == null ? '—' : formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'netValue',
        header: 'Net Value',
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {value == null ? '—' : formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'asp',
        header: 'ASP',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {value > 0 ? formatCurrency(value) : '—'}
            </span>
        ),
    },
    {
        key: 'mrp',
        header: 'MRP',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {value != null && value > 0 ? formatCurrency(value) : '—'}
            </span>
        ),
    },
    {
        key: 'growth',
        header: 'Growth',
        width: 110,
        align: 'right',
        render: (value: string) => {
            const dir = trendDirection(value);
            const color =
                dir === 'up'
                    ? 'rgb(5, 150, 105)'
                    : dir === 'down'
                      ? 'rgb(220, 38, 38)'
                      : 'rgb(100, 116, 139)';
            return <span style={{ color, fontWeight: 500 }}>{value}</span>;
        },
    },
];

/* -------------------------------------------------------------------------- */
/*                       Period column builders                                */
/* -------------------------------------------------------------------------- */

/** `startDate` is what decides returns eligibility: the returns feed starts on
 *  a fixed date, so each period column is judged on its own start, not on the
 *  card range's. A May-to-July range has one ineligible column and two eligible
 *  ones. */
type PeriodBucket = { key: string; label: string; startDate: string };

/** A period column shows Return Units only if the whole period sits inside the
 *  returns window; otherwise "-", so a pre-window month never reads as a real 0. */
const periodHasReturns = (
    period: PeriodBucket,
    returnsAvailableFrom?: string,
): boolean =>
    !!returnsAvailableFrom && period.startDate >= returnsAvailableFrom;

const toPeriodType = (periodType: 'monthly' | 'weekly'): PeriodType =>
    periodType === 'monthly' ? 'MONTH' : 'WEEK';

/** Extract sorted unique period buckets from rows keyed by DATE grouping. */
function extractPeriods(
    rows: SalesAggregateRow[],
    periodType: 'monthly' | 'weekly',
): PeriodBucket[] {
    const type = toPeriodType(periodType);
    const seen = new Map<
        string,
        { label: string; order: number; startDate: string }
    >();
    for (const row of rows) {
        const dateKey = normalizeDateKey(row.grouping?.DATE?.key ?? '');
        if (!dateKey) continue;
        const meta = getPeriodKeyMeta(dateKey, type);
        if (!seen.has(meta.key))
            seen.set(meta.key, {
                label: meta.label,
                order: meta.order,
                startDate: meta.startDate,
            });
    }
    return Array.from(seen.entries())
        .sort(([, a], [, b]) => a.order - b.order)
        .map(([key, { label, startDate }]) => ({ key, label, startDate }));
}

/** Group rows into Map<entityKey, Map<periodKey, {revenue, units}>> */
type PeriodCell = { revenue: number; units: number; returnUnits: number };

function bucketByEntityAndPeriod(
    rows: SalesAggregateRow[],
    entityGrouping: 'CHANNEL' | 'PRODUCT',
    periodType: 'monthly' | 'weekly',
): Map<string, Map<string, PeriodCell>> {
    const type = toPeriodType(periodType);
    const result = new Map<string, Map<string, PeriodCell>>();
    for (const row of rows) {
        const entityKey = row.grouping?.[entityGrouping]?.key;
        const dateKey = normalizeDateKey(row.grouping?.DATE?.key ?? '');
        if (!entityKey || !dateKey) continue;
        const periodKey = getPeriodKeyMeta(dateKey, type).key;

        if (!result.has(entityKey)) result.set(entityKey, new Map());
        const periods = result.get(entityKey)!;
        const entry = periods.get(periodKey) ?? {
            revenue: 0,
            units: 0,
            returnUnits: 0,
        };
        entry.revenue += row.data.revenue || 0;
        entry.units += row.data.quantity || 0;
        entry.returnUnits += row.data.returnQuantity || 0;
        periods.set(periodKey, entry);
    }
    return result;
}

function buildChannelPeriodColumns(
    periods: PeriodBucket[],
    periodData: Map<string, Map<string, PeriodCell>>,
    revenueMode: RevenueMode,
    returnsAvailableFrom?: string,
): DataSheetColumn<ChannelRow>[] {
    const cols: DataSheetColumn<ChannelRow>[] = [
        {
            key: 'channelId',
            header: 'Channel',
            width: 160,
            sticky: true,
            render: (value: string) => (
                <span style={{ fontWeight: 600, color: 'rgb(26, 32, 44)' }}>
                    {value}
                </span>
            ),
        },
    ];

    for (const p of periods) {
        cols.push({
            key: `period_${p.key}_revenue`,
            header: revenueColumnHeader(revenueMode),
            group: p.label,
            width: 110,
            align: 'right',
            render: (_: unknown, row: ChannelRow) => {
                const val =
                    periodData.get(row.channelId)?.get(p.key)?.revenue ?? 0;
                return (
                    <span
                        style={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatCurrency(val)}
                    </span>
                );
            },
        });
        cols.push({
            key: `period_${p.key}_units`,
            header: 'Units',
            group: p.label,
            width: 80,
            align: 'right',
            render: (_: unknown, row: ChannelRow) => {
                const val =
                    periodData.get(row.channelId)?.get(p.key)?.units ?? 0;
                return (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatCount(val)}
                    </span>
                );
            },
        });
        cols.push({
            key: `period_${p.key}_returnUnits`,
            header: 'Return Units',
            group: p.label,
            width: 100,
            align: 'right',
            render: (_: unknown, row: ChannelRow) => {
                if (!periodHasReturns(p, returnsAvailableFrom)) return '—';
                const val =
                    periodData.get(row.channelId)?.get(p.key)?.returnUnits ?? 0;
                return (
                    <span
                        style={{
                            fontVariantNumeric: 'tabular-nums',
                            color: 'rgb(100, 116, 139)',
                        }}
                    >
                        {formatCount(val)}
                    </span>
                );
            },
        });
    }

    return cols;
}

function buildSkuPeriodColumns(
    periods: PeriodBucket[],
    periodData: Map<string, Map<string, PeriodCell>>,
    rankOffset = 0,
    revenueMode: RevenueMode = 'reported',
    returnsAvailableFrom?: string,
): DataSheetColumn<SkuRow>[] {
    const cols: DataSheetColumn<SkuRow>[] = [
        {
            key: 'rank',
            header: '#',
            width: 50,
            align: 'center',
            render: (_v: unknown, _row: SkuRow, idx: number) => (
                <span style={{ color: 'rgb(100, 116, 139)', fontWeight: 500 }}>
                    {rankOffset + idx + 1}
                </span>
            ),
        },
        {
            key: 'name',
            header: 'SKU',
            width: 260,
            sticky: true,
            render: (_v: unknown, row: SkuRow) => (
                <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    <span
                        style={{
                            fontWeight: 600,
                            color: 'rgb(26, 32, 44)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {row.name || '—'}
                    </span>
                    <span
                        style={{
                            fontSize: 11,
                            color: 'rgb(100, 116, 139)',
                            fontFamily: 'monospace',
                        }}
                    >
                        {row.sku || '—'}
                    </span>
                </div>
            ),
        },
    ];

    for (const p of periods) {
        cols.push({
            key: `period_${p.key}_revenue`,
            header: revenueColumnHeader(revenueMode),
            group: p.label,
            width: 110,
            align: 'right',
            render: (_: unknown, row: SkuRow) => {
                const val =
                    periodData.get(row.productId)?.get(p.key)?.revenue ?? 0;
                return (
                    <span
                        style={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatCurrency(val)}
                    </span>
                );
            },
        });
        cols.push({
            key: `period_${p.key}_units`,
            header: 'Units',
            group: p.label,
            width: 80,
            align: 'right',
            render: (_: unknown, row: SkuRow) => {
                const val =
                    periodData.get(row.productId)?.get(p.key)?.units ?? 0;
                return (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatCount(val)}
                    </span>
                );
            },
        });
        cols.push({
            key: `period_${p.key}_returnUnits`,
            header: 'Return Units',
            group: p.label,
            width: 100,
            align: 'right',
            render: (_: unknown, row: SkuRow) => {
                if (!periodHasReturns(p, returnsAvailableFrom)) return '—';
                const val =
                    periodData.get(row.productId)?.get(p.key)?.returnUnits ?? 0;
                return (
                    <span
                        style={{
                            fontVariantNumeric: 'tabular-nums',
                            color: 'rgb(100, 116, 139)',
                        }}
                    >
                        {formatCount(val)}
                    </span>
                );
            },
        });
    }

    return cols;
}

/* -------------------------------------------------------------------------- */
/*                            Breakdown row type                               */
/* -------------------------------------------------------------------------- */

type BreakdownRow = {
    key: string;
    label: string;
    revenue: number;
    units: number;
    share: number;
    growth: string;
    returnUnits?: number;
    netUnits?: number;
};

const buildBreakdownColumns = (
    revenueMode: RevenueMode,
): DataSheetColumn<BreakdownRow>[] => [
    {
        key: 'label',
        header: 'Name',
        width: 240,
        sticky: true,
        render: (value: string) => (
            <span style={{ fontWeight: 600, color: 'rgb(26, 32, 44)' }}>
                {value}
            </span>
        ),
    },
    {
        key: 'revenue',
        header: revenueColumnHeader(revenueMode),
        width: 130,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {formatCurrency(value)}
            </span>
        ),
    },
    {
        key: 'units',
        header: 'Units',
        width: 100,
        align: 'right',
        sortable: true,
        render: (value: number) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {formatCount(value) ?? '—'}
            </span>
        ),
    },
    {
        key: 'returnUnits',
        header: 'Return Units',
        width: 120,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span
                style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'rgb(100, 116, 139)',
                }}
            >
                {value == null ? '—' : formatCount(value)}
            </span>
        ),
    },
    {
        key: 'netUnits',
        header: 'Net Units',
        width: 110,
        align: 'right',
        sortable: true,
        render: (value: number | undefined) => (
            <span
                style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            >
                {value == null ? '—' : formatCount(value)}
            </span>
        ),
    },
    {
        key: 'share',
        header: 'Share',
        width: 90,
        align: 'right',
        render: (value: number) => `${value.toFixed(1)}%`,
    },
    {
        key: 'growth',
        header: 'Growth',
        width: 110,
        align: 'right',
        render: (value: string) => {
            const dir = trendDirection(value);
            const color =
                dir === 'up'
                    ? 'rgb(5, 150, 105)'
                    : dir === 'down'
                      ? 'rgb(220, 38, 38)'
                      : 'rgb(100, 116, 139)';
            return <span style={{ color, fontWeight: 500 }}>{value}</span>;
        },
    },
];

/* -------------------------------------------------------------------------- */
/*                              Main component                                 */
/* -------------------------------------------------------------------------- */

const SalesContainer: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedChannelId = searchParams.get('channelId');
    const selectedSkuId = searchParams.get('skuId');

    const { captureContext, addToChat } = useWidgetContextCapture({
        componentName: 'SalesContainer',
        label: 'Sales',
    });

    /* ----------------------------- Filter state ----------------------------- */

    const [range, setRange] = usePersistentState<DateRange>(
        'sales_range',
        defaultLast4Weeks(),
    );
    const [channelFilter, setChannelFilter] = usePersistentState<FilterValue>(
        'sales_channel_filter',
        {},
    );
    const [productFilter, setProductFilter] = usePersistentState<FilterValue>(
        'sales_product_filter',
        {},
    );
    const [showCalendar, setShowCalendar] = useState(false);
    const [channelOptions, setChannelOptions] = useState<
        { label: string; value: string }[]
    >([]);
    const [rawChannels, setRawChannels] = useState<ChannelOptionResponse[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<
        { label: string; value: string }[]
    >([]);
    const [productGroupOptions, setProductGroupOptions] = useState<
        { label: string; value: string }[]
    >([]);
    const [tierOptions, setTierOptions] = useState<
        { label: string; value: string }[]
    >([]);

    /* ----------------------------- Data state ----------------------------- */

    const [trendRows, setTrendRows] = useState<SalesAggregateRow[]>([]);
    const [prevTrendRows, setPrevTrendRows] = useState<SalesAggregateRow[]>([]);
    const [channelRows, setChannelRows] = useState<SalesAggregateRow[]>([]);
    const [channelMixRows, setChannelMixRows] = useState<SalesAggregateRow[]>(
        [],
    );
    const [prevChannelRows, setPrevChannelRows] = useState<SalesAggregateRow[]>(
        [],
    );

    /* Performance-card data. The SKU and Channel sheets share one date range and
     * one set of filters, and only this card's effect writes these — sharing a
     * state slot with the page range is what made the period views disagree with
     * the rows next to them. */
    const [channelCardRows, setChannelCardRows] = useState<SalesAggregateRow[]>(
        [],
    );
    const [channelCardPrevRows, setChannelCardPrevRows] = useState<
        SalesAggregateRow[]
    >([]);
    const [channelPeriodRows, setChannelPeriodRows] = useState<
        SalesAggregateRow[]
    >([]);
    const [skuCardRows, setSkuCardRows] = useState<SalesAggregateRow[]>([]);
    const [skuCardPrevRows, setSkuCardPrevRows] = useState<SalesAggregateRow[]>(
        [],
    );
    const [skuPeriodRows, setSkuPeriodRows] = useState<SalesAggregateRow[]>([]);

    const [dailyRunRate, setDailyRunRate] = useState(0);
    const [drrTrend, setDrrTrend] = useState<string>('—');
    const [drrTrendDir, setDrrTrendDir] = useState<'up' | 'down' | 'neutral'>(
        'neutral',
    );
    const [dailyRevenueRunRate, setDailyRevenueRunRate] = useState(0);
    const [revenueMode, setRevenueMode] = useState<RevenueMode>('reported');
    const [returnsAvailableFrom, setReturnsAvailableFrom] = useState<
        string | undefined
    >(undefined);
    const [channelHiddenCols, setChannelHiddenCols] = usePersistentState<
        string[]
    >('sales_channel_hidden_cols', []);
    const [skuHiddenCols, setSkuHiddenCols] = usePersistentState<string[]>(
        'sales_sku_hidden_cols',
        [],
    );
    const [loading, setLoading] = useState(false);
    const [loadingCard, setLoadingCard] = useState(false);
    const mainFetchRef = useRef(true);
    const cardFetchRef = useRef(true);

    /* ---- Toggle states ---- */
    const [trendMode, setTrendMode] = useState<'revenue' | 'units'>('revenue');
    const [channelMixMode, setChannelMixMode] = useState<'units' | 'revenue'>(
        'revenue',
    );
    const [showComparison, setShowComparison] = useState(false);

    /* ---- Performance card: one scope shared by the SKU and Channel sheets ---- */
    const [cardTab, setCardTab] = usePersistentState<CardTab>(
        'sales_card_tab',
        'sku',
    );
    const [cardTrendPeriod, setCardTrendPeriod] =
        useState<TrendPeriod>('summary');
    /* `null` means "follow the page range" — the card shows the page dates and
     * tracks them until the user picks a range on the card itself, at which
     * point it detaches and the ✕ puts it back. */
    const [cardRangeOverride, setCardRangeOverride] = usePersistentState<
        PeriodDateRange | null
    >('sales_card_range', null);
    const cardRange = cardRangeOverride ?? range;
    const isSkuTab = cardTab === 'sku';

    const [cardChannelFilter, setCardChannelFilter] = useState<string[]>([]);
    const [cardProductFilter, setCardProductFilter] =
        usePersistentState<FilterValue>('sales_card_product_filter', {});
    const [cardSearch, setCardSearch] = usePersistentState<string>(
        'sales_card_search',
        '',
    );

    /* ---- SKU sheet only ---- */
    const [skuMode, setSkuMode] = useState('top');

    /* ---- Bundle reporting (per brand; off = the page as it always was) ---- */
    const bundleReporting = useBundleReporting();
    const [bundleViewChoice, setBundleViewChoice] =
        usePersistentState<BundleView | null>('sales_bundle_view', null);
    const bundleView: BundleView =
        bundleViewChoice ?? bundleReporting.defaultView;
    const [skuBundleRows, setSkuBundleRows] = useState<SalesAggregateRow[]>(
        [],
    );
    const [bundleEdges, setBundleEdges] = useState<ProductRelationEdge[]>([]);
    const [skuPage, setSkuPage] = useState(1);
    const [skuPageSize, setSkuPageSize] = useState(20);

    const filterOptions = useMemo(
        () => ({ categoryOptions, productGroupOptions, tierOptions }),
        [categoryOptions, productGroupOptions, tierOptions],
    );
    const { params: productFilterParams } = useProductFilterParams(
        filterOptions,
        productFilter,
    );
    const { params: cardProductFilterParams } = useProductFilterParams(
        filterOptions,
        cardProductFilter,
    );

    /* ---- Pane state ---- */
    const [paneGroupBy, setPaneGroupBy] = useState<GroupByOption>('product');
    const [paneTrendRows, setPaneTrendRows] = useState<SalesAggregateRow[]>([]);
    const [paneProductRows, setPaneProductRows] = useState<SalesAggregateRow[]>(
        [],
    );
    const [panePrevProductRows, setPanePrevProductRows] = useState<
        SalesAggregateRow[]
    >([]);
    const [loadingPane, setLoadingPane] = useState(false);

    /* ---- SKU pane state ---- */
    const [skuPaneChannelRows, setSkuPaneChannelRows] = useState<
        SalesAggregateRow[]
    >([]);
    const [skuPanePrevChannelRows, setSkuPanePrevChannelRows] = useState<
        SalesAggregateRow[]
    >([]);
    const [loadingSkuPane, setLoadingSkuPane] = useState(false);

    /* ---- AI Insights state ---- */
    const [insightsData, setInsightsData] = useState<SalesInsightItem[]>([]);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [insightsCollapsed, setInsightsCollapsed] = usePersistentState(
        'sales_insights_collapsed',
        false,
    );
    /* ---------------------------------------------------------------------- */
    /*                        Fetch: channel options                           */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        const fetchChannels = async () => {
            try {
                const channels =
                    ((await ReparoApi({
                        method: 'GET',
                        url: '/channels?limit=200',
                        body: undefined,
                    })) as unknown as ChannelOptionResponse[] | null) ?? [];

                const mapped = channels
                    .map((c) => {
                        const value =
                            c.abbreviationKey || c.channelId || c.name;
                        const label =
                            c.abbreviationKey || c.name || c.channelId;
                        if (!value || !label) return null;
                        return { label, value: String(value) };
                    })
                    .filter(
                        (opt): opt is { label: string; value: string } =>
                            opt !== null,
                    );

                const deduped = Array.from(
                    new Map(mapped.map((o) => [o.value, o])).values(),
                );
                setChannelOptions(deduped);
                setRawChannels(channels);
            } catch {
                setChannelOptions([]);
                setRawChannels([]);
            }
        };

        void fetchChannels();
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/products/categories/list',
                        body: null,
                    })) as unknown as string[] | null) ?? [];

                const mapped = categories
                    .map((c) => c?.trim())
                    .filter(Boolean)
                    .map((c) => ({ label: c as string, value: c as string }));

                setCategoryOptions(
                    Array.from(
                        new Map(mapped.map((o) => [o.value, o])).values(),
                    ),
                );
            } catch {
                setCategoryOptions([]);
            }
        };

        void fetchCategories();
    }, []);

    useEffect(() => {
        const fetchProductGroups = async () => {
            try {
                const groups =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/products/product-groups/list',
                        body: null,
                    })) as unknown as string[] | null) ?? [];

                const mapped = groups
                    .map((g) => g?.trim())
                    .filter(Boolean)
                    .map((g) => ({ label: g as string, value: g as string }));

                setProductGroupOptions(
                    Array.from(
                        new Map(mapped.map((o) => [o.value, o])).values(),
                    ),
                );
            } catch {
                setProductGroupOptions([]);
            }
        };

        void fetchProductGroups();
    }, []);

    const tierChannelKey = useMemo(() => {
        const channels = channelFilter.channel ?? [];
        return channels.length === 1 ? channels[0] : undefined;
    }, [channelFilter.channel]);

    useEffect(() => {
        const fetchTiers = async () => {
            try {
                const tiers =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/products/tiers/list',
                        body: JSON.stringify({ channel: tierChannelKey }),
                    })) as unknown as string[] | null) ?? [];

                const mapped = tiers
                    .map((t) => t?.trim())
                    .filter(Boolean)
                    .map((t) => ({ label: t as string, value: t as string }));

                setTierOptions(
                    Array.from(
                        new Map(mapped.map((o) => [o.value, o])).values(),
                    ),
                );
            } catch {
                setTierOptions([]);
            }
        };

        void fetchTiers();
    }, [tierChannelKey]);

    /* ---------------------------------------------------------------------- */
    /*                         Fetch: main data                                */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        const timer = setTimeout(() => {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    setTrendRows([]);
                    setChannelRows([]);
                    setChannelMixRows([]);
                    setPrevChannelRows([]);

                    const channels = channelFilter.channel ?? [];
                    const categories = productFilter.category ?? [];
                    const productGroups = productFilter.productGroup ?? [];
                    const tiers = productFilter.tier ?? [];
                    const isActive = toIsActive(productFilter.isActive);
                    const attrProductIds =
                        await resolveAttributeFilterEntityIds(
                            productFilter,
                            getMatchingEntityIds,
                        );
                    const prevRange = buildPreviousRange(range);
                    const base = {
                        startDate: range.startDate,
                        endDate: range.endDate,
                        channels,
                        categories,
                        productGroups,
                        tiers,
                        isActive,
                        ...(attrProductIds !== null && {
                            productIds: attrProductIds,
                        }),
                    };

                    const prevBase = {
                        ...base,
                        startDate: prevRange.startDate,
                        endDate: prevRange.endDate,
                    };

                    // Compute yesterday for DRR trend
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = yesterday.toISOString().split('T')[0];

                    const results = await Promise.allSettled([
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                ...base,
                                groupings: ['DATE'],
                                periodType: 'DAY',
                                includeReturns: true,
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                ...base,
                                groupings: ['CHANNEL'],
                                includeReturns: true,
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                ...prevBase,
                                groupings: ['CHANNEL'],
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                ...base,
                                groupings: ['DATE', 'CHANNEL'],
                                periodType: 'WEEK',
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/trend-calculation',
                            dedupe: true,
                            body: JSON.stringify({
                                service_name: 'salesService',
                                method_name: 'getSalesAggregate',
                                method_params: {
                                    start_date: yesterdayStr,
                                    end_date: yesterdayStr,
                                    groupings: ['PRODUCT'],
                                },
                                evaluated_key: 'daily_run_rate',
                                comparison_period: 'SAME_DAY_LAST_WEEK',
                            }),
                        }) as unknown as Promise<TrendResponse>,
                    ]);

                    const trendResp =
                        results[0].status === 'fulfilled'
                            ? results[0].value
                            : null;
                    const channelResp =
                        results[1].status === 'fulfilled'
                            ? results[1].value
                            : null;
                    const prevChannelResp =
                        results[2].status === 'fulfilled'
                            ? results[2].value
                            : null;
                    const channelMixResp =
                        results[3].status === 'fulfilled'
                            ? results[3].value
                            : null;
                    const drrResp =
                        results[4].status === 'fulfilled'
                            ? results[4].value
                            : null;
                    setTrendRows(trendResp?.data ?? []);
                    setChannelRows(channelResp?.data ?? []);
                    setChannelMixRows(
                        (channelMixResp as SalesAggregateResponse)?.data ?? [],
                    );
                    setPrevChannelRows(prevChannelResp?.data ?? []);
                    setDailyRunRate(channelResp?.daily_run_rate ?? 0);
                    setDailyRevenueRunRate(
                        channelResp?.daily_revenue_run_rate ?? 0,
                    );
                    setRevenueMode(channelResp?.revenueMode ?? 'reported');
                    setReturnsAvailableFrom(channelResp?.returnsAvailableFrom);

                    if (drrResp?.trend != null) {
                        const sign = drrResp.trend > 0 ? '+' : '';
                        setDrrTrend(
                            `${sign}${Math.round(drrResp.trend * 10) / 10}%`,
                        );
                        setDrrTrendDir(
                            drrResp.trend > 0
                                ? 'up'
                                : drrResp.trend < 0
                                  ? 'down'
                                  : 'neutral',
                        );
                    } else {
                        setDrrTrend('—');
                        setDrrTrendDir('neutral');
                    }

                    captureContext({
                        start_date: range.startDate,
                        end_date: range.endDate,
                        channels,
                    });
                } catch (err) {
                    console.error('Sales fetch error', err);
                } finally {
                    setLoading(false);
                }
            };

            void fetchData();
        }, debounceFor(mainFetchRef));
        return () => clearTimeout(timer);
    }, [range, channelFilter, productFilter]);

    /* ---------------------------------------------------------------------- */
    /*              Fetch: previous-period trend (comparison only)              */
    /* ---------------------------------------------------------------------- */

    /* Only the chart's Compare overlay reads prevTrendRows, and that toggle
     * starts off. Fetching this with the main batch spent a full scan per page
     * load on rows that were thrown away on the next line. */
    useEffect(() => {
        if (!showComparison) return;

        const timer = setTimeout(() => {
            const fetchPrevTrend = async () => {
                try {
                    const attrProductIds =
                        await resolveAttributeFilterEntityIds(
                            productFilter,
                            getMatchingEntityIds,
                        );
                    const prevRange = buildPreviousRange(range);
                    const resp = (await ReparoApi({
                        method: 'POST',
                        url: '/sales/aggregate',
                        dedupe: true,
                        body: JSON.stringify({
                            startDate: prevRange.startDate,
                            endDate: prevRange.endDate,
                            channels: channelFilter.channel ?? [],
                            categories: productFilter.category ?? [],
                            productGroups: productFilter.productGroup ?? [],
                            tiers: productFilter.tier ?? [],
                            isActive: toIsActive(productFilter.isActive),
                            ...(attrProductIds !== null && {
                                productIds: attrProductIds,
                            }),
                            groupings: ['DATE'],
                            periodType: 'DAY',
                        }),
                    })) as unknown as SalesAggregateResponse;
                    setPrevTrendRows(resp?.data ?? []);
                } catch (err) {
                    console.error('Comparison trend fetch error', err);
                    setPrevTrendRows([]);
                }
            };

            void fetchPrevTrend();
        }, 350);
        return () => clearTimeout(timer);
    }, [showComparison, range, channelFilter, productFilter]);

    /* ---------------------------------------------------------------------- */
    /*                      Fetch: Performance card                             */
    /* ---------------------------------------------------------------------- */

    /* Everything both sheets render comes from here — summary cells, growth,
     * growth and the period columns all share one date range and one set of
     * filters, so the rows can never be scoped differently from the numbers
     * beside them.
     *
     * Both summaries are always fetched, even for the sheet that isn't showing:
     * the AI insights need the SKU and Channel tables together. The period query
     * runs only for the active sheet, and only outside Summary. */
    useEffect(() => {
        const timer = setTimeout(() => {
            const fetchCard = async () => {
                try {
                    setLoadingCard(true);
                    const isPeriodView = cardTrendPeriod !== 'summary';
                    const isSku = cardTab === 'sku';
                    const withBundles =
                        bundleReporting.enabled && !isPeriodView;
                    const entity = isSku ? 'PRODUCT' : 'CHANNEL';
                    const attrProductIds =
                        await resolveAttributeFilterEntityIds(
                            cardProductFilter,
                            getMatchingEntityIds,
                        );
                    const prevRange = buildPreviousRange(cardRange);
                    const base = {
                        startDate: cardRange.startDate,
                        endDate: cardRange.endDate,
                        channels: cardChannelFilter,
                        categories: cardProductFilter.category ?? [],
                        productGroups: cardProductFilter.productGroup ?? [],
                        tiers: cardProductFilter.tier ?? [],
                        isActive: toIsActive(cardProductFilter.isActive),
                        ...(attrProductIds !== null && {
                            productIds: attrProductIds,
                        }),
                    };
                    const prevBase = {
                        ...base,
                        startDate: prevRange.startDate,
                        endDate: prevRange.endDate,
                    };
                    const aggregate = (body: Record<string, unknown>) =>
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify(body),
                        }) as unknown as Promise<SalesAggregateResponse>;

                    const results = await Promise.allSettled([
                        aggregate({
                            ...base,
                            groupings: ['PRODUCT'],
                            includeReturns: true,
                        }),
                        aggregate({ ...prevBase, groupings: ['PRODUCT'] }),
                        aggregate({
                            ...base,
                            groupings: ['CHANNEL'],
                            includeReturns: true,
                        }),
                        aggregate({ ...prevBase, groupings: ['CHANNEL'] }),
                        ...(isPeriodView
                            ? [
                                  aggregate({
                                      ...base,
                                      groupings: ['DATE', entity],
                                      includeReturns: true,
                                      periodType:
                                          cardTrendPeriod === 'weekly'
                                              ? 'WEEK'
                                              : 'MONTH',
                                  }),
                              ]
                            : []),
                        // Bundle reporting: the same product rows, split by the
                        // bundle each unit was sold in. Summary only.
                        ...(withBundles
                            ? [
                                  aggregate({
                                      ...base,
                                      groupings: ['PRODUCT'],
                                      splitByBundle: true,
                                  }),
                              ]
                            : []),
                    ]);

                    const dataAt = (i: number) =>
                        results[i]?.status === 'fulfilled'
                            ? ((
                                  results[
                                      i
                                  ] as PromiseFulfilledResult<SalesAggregateResponse>
                              ).value?.data ?? [])
                            : [];

                    setSkuCardRows(dataAt(0));
                    setSkuCardPrevRows(dataAt(1));
                    setChannelCardRows(dataAt(2));
                    setChannelCardPrevRows(dataAt(3));

                    const period = isPeriodView ? dataAt(4) : [];
                    setSkuPeriodRows(isSku ? period : []);
                    setChannelPeriodRows(isSku ? [] : period);
                    setSkuBundleRows(withBundles ? dataAt(4) : []);
                } catch (err) {
                    console.error('Performance card fetch error', err);
                } finally {
                    setLoadingCard(false);
                }
            };

            void fetchCard();
        }, debounceFor(cardFetchRef));
        return () => clearTimeout(timer);
    }, [
        cardRange,
        cardTab,
        cardTrendPeriod,
        cardChannelFilter,
        cardProductFilter,
        bundleReporting.enabled,
    ]);

    /* Bundle recipes (for "2 × Dream On 30" and units per bundle). The shared,
     * once-per-session relation graph the SKU explosion already loads. */
    useEffect(() => {
        if (!bundleReporting.enabled) return;
        loadRelations()
            .then(setBundleEdges)
            .catch((err) => console.error('Bundle recipes fetch error', err));
    }, [bundleReporting.enabled]);

    /* ---------------------------------------------------------------------- */
    /*                       Fetch: channel pane data                          */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        if (!selectedChannelId) {
            setPaneTrendRows([]);
            setPaneProductRows([]);
            setPanePrevProductRows([]);
            return;
        }

        const timer = setTimeout(() => {
            const fetchPaneData = async () => {
                try {
                    setLoadingPane(true);
                    const prevRange = buildPreviousRange(range);

                    const results = await Promise.allSettled([
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                startDate: range.startDate,
                                endDate: range.endDate,
                                channels: [selectedChannelId],
                                categories: [],
                                productGroups: [],
                                tiers: [],
                                isActive: true,
                                groupings: ['DATE'],
                                periodType: 'DAY',
                                includeReturns: true,
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                startDate: range.startDate,
                                endDate: range.endDate,
                                channels: [selectedChannelId],
                                categories: [],
                                productGroups: [],
                                tiers: [],
                                isActive: true,
                                groupings: ['PRODUCT'],
                                includeReturns: true,
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                startDate: prevRange.startDate,
                                endDate: prevRange.endDate,
                                channels: [selectedChannelId],
                                categories: [],
                                productGroups: [],
                                tiers: [],
                                isActive: true,
                                groupings: ['PRODUCT'],
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                    ]);

                    setPaneTrendRows(
                        results[0].status === 'fulfilled'
                            ? (results[0].value?.data ?? [])
                            : [],
                    );
                    setPaneProductRows(
                        results[1].status === 'fulfilled'
                            ? (results[1].value?.data ?? [])
                            : [],
                    );
                    setPanePrevProductRows(
                        results[2].status === 'fulfilled'
                            ? (results[2].value?.data ?? [])
                            : [],
                    );
                } catch (err) {
                    console.error('Pane fetch error', err);
                } finally {
                    setLoadingPane(false);
                }
            };

            void fetchPaneData();
        }, 200);
        return () => clearTimeout(timer);
    }, [selectedChannelId, range]);

    /* ---------------------------------------------------------------------- */
    /*                       Fetch: SKU pane data                              */
    /* ---------------------------------------------------------------------- */

    useEffect(() => {
        if (!selectedSkuId) {
            setSkuPaneChannelRows([]);
            setSkuPanePrevChannelRows([]);
            return;
        }

        const timer = setTimeout(() => {
            const fetchSkuPaneData = async () => {
                try {
                    setLoadingSkuPane(true);
                    const prevRange = buildPreviousRange(range);

                    const results = await Promise.allSettled([
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                startDate: range.startDate,
                                endDate: range.endDate,
                                productIds: [selectedSkuId],
                                channels: [],
                                categories: [],
                                productGroups: [],
                                tiers: [],
                                isActive: true,
                                groupings: ['CHANNEL'],
                                includeReturns: true,
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                        ReparoApi({
                            method: 'POST',
                            url: '/sales/aggregate',
                            dedupe: true,
                            body: JSON.stringify({
                                startDate: prevRange.startDate,
                                endDate: prevRange.endDate,
                                productIds: [selectedSkuId],
                                channels: [],
                                categories: [],
                                productGroups: [],
                                tiers: [],
                                isActive: true,
                                groupings: ['CHANNEL'],
                            }),
                        }) as unknown as Promise<SalesAggregateResponse>,
                    ]);

                    setSkuPaneChannelRows(
                        results[0].status === 'fulfilled'
                            ? (results[0].value?.data ?? [])
                            : [],
                    );
                    setSkuPanePrevChannelRows(
                        results[1].status === 'fulfilled'
                            ? (results[1].value?.data ?? [])
                            : [],
                    );
                } catch (err) {
                    console.error('SKU pane fetch error', err);
                } finally {
                    setLoadingSkuPane(false);
                }
            };

            void fetchSkuPaneData();
        }, 200);
        return () => clearTimeout(timer);
    }, [selectedSkuId, range]);

    /* ---------------------------------------------------------------------- */
    /*                           Derived: KPI values                           */
    /* ---------------------------------------------------------------------- */

    const totalUnits = useMemo(
        () =>
            channelRows.reduce((sum, row) => sum + (row.data.quantity || 0), 0),
        [channelRows],
    );

    const prevTotalUnits = useMemo(
        () =>
            prevChannelRows.reduce(
                (sum, row) => sum + (row.data.quantity || 0),
                0,
            ),
        [prevChannelRows],
    );

    const unitsGrowth = computeGrowthLabel(totalUnits, prevTotalUnits);

    const totalRevenue = useMemo(
        () =>
            channelRows.reduce((sum, row) => sum + (row.data.revenue || 0), 0),
        [channelRows],
    );

    const prevTotalRevenue = useMemo(
        () =>
            prevChannelRows.reduce(
                (sum, row) => sum + (row.data.revenue || 0),
                0,
            ),
        [prevChannelRows],
    );

    const revenueGrowth = computeGrowthLabel(totalRevenue, prevTotalRevenue);

    // Returns/net totals. null (channel not instrumented for returns) counts as
    // 0 toward the total, so net = gross for those channels.
    const totalReturnValue = useMemo(
        () =>
            channelRows.reduce(
                (sum, row) => sum + (row.data.returnValue ?? 0),
                0,
            ),
        [channelRows],
    );

    const totalReturnUnits = useMemo(
        () =>
            channelRows.reduce(
                (sum, row) => sum + (row.data.returnQuantity ?? 0),
                0,
            ),
        [channelRows],
    );

    const totalNetValue = totalRevenue - totalReturnValue;
    const totalNetUnits = totalUnits - totalReturnUnits;

    // Returns/net are only meaningful when the whole selected range sits inside
    // the returns window (start >= returnsAvailableFrom). For a range starting
    // earlier (May, or a May-June straddle), returns render as "-" so it reads
    // as "no data" rather than a misleading zero.
    const returnsAvailable =
        !!returnsAvailableFrom && range.startDate >= returnsAvailableFrom;
    // The Performance card can be on its own range, so it needs its own gate —
    // otherwise a card range predating the returns window would render 0s.
    const cardReturnsAvailable =
        !!returnsAvailableFrom && cardRange.startDate >= returnsAvailableFrom;
    const returnsFromLabel = returnsAvailableFrom
        ? new Date(returnsAvailableFrom).toLocaleDateString('en-GB', {
              month: 'short',
              year: 'numeric',
          })
        : '';

    /** Set of B2C channel abbreviation keys for AOV calculation */
    const b2cChannelKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const ch of rawChannels) {
            if (ch.type === 'B2C' && ch.abbreviationKey) {
                keys.add(ch.abbreviationKey);
            }
        }
        return keys;
    }, [rawChannels]);

    const b2cAov = useMemo(() => {
        let b2cRevenue = 0;
        let b2cOrders = 0;
        for (const row of channelRows) {
            const key = row.grouping?.CHANNEL?.key;
            if (key && b2cChannelKeys.has(key)) {
                b2cRevenue += row.data.revenue || 0;
                b2cOrders += row.data.orders || 0;
            }
        }
        return b2cOrders > 0 ? b2cRevenue / b2cOrders : 0;
    }, [channelRows, b2cChannelKeys]);

    const prevB2cAov = useMemo(() => {
        let b2cRevenue = 0;
        let b2cOrders = 0;
        for (const row of prevChannelRows) {
            const key = row.grouping?.CHANNEL?.key;
            if (key && b2cChannelKeys.has(key)) {
                b2cRevenue += row.data.revenue || 0;
                b2cOrders += row.data.orders || 0;
            }
        }
        return b2cOrders > 0 ? b2cRevenue / b2cOrders : 0;
    }, [prevChannelRows, b2cChannelKeys]);

    const aovGrowth = computeGrowthLabel(b2cAov, prevB2cAov);

    /* ---------------------------------------------------------------------- */
    /*                         Derived: trend chart data                       */
    /* ---------------------------------------------------------------------- */

    const chartData = useMemo<SalesChartPoint[]>(() => {
        const bucket = new Map<
            string,
            {
                units: number;
                revenue: number;
                returnUnits: number;
                returnRevenue: number;
            }
        >();
        for (const row of trendRows) {
            const dateKey = normalizeDateKey(row.grouping?.DATE?.key ?? '');
            if (!dateKey) continue;
            const entry = bucket.get(dateKey) ?? {
                units: 0,
                revenue: 0,
                returnUnits: 0,
                returnRevenue: 0,
            };
            entry.units += row.data.quantity || 0;
            entry.revenue += row.data.revenue || 0;
            entry.returnUnits += row.data.returnQuantity ?? 0;
            entry.returnRevenue += row.data.returnValue ?? 0;
            bucket.set(dateKey, entry);
        }
        const currentPoints = Array.from(bucket.entries())
            .map(([date, vals]) => ({
                date,
                units: vals.units,
                revenue: vals.revenue,
                returnUnits: vals.returnUnits,
                returnRevenue: vals.returnRevenue,
                netUnits: vals.units - vals.returnUnits,
                netRevenue: vals.revenue - vals.returnRevenue,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));

        if (!showComparison || prevTrendRows.length === 0) return currentPoints;

        // Build previous period bucket keyed by date
        const prevBucket = new Map<
            string,
            { units: number; revenue: number }
        >();
        for (const row of prevTrendRows) {
            const dateKey = normalizeDateKey(row.grouping?.DATE?.key ?? '');
            if (!dateKey) continue;
            const entry = prevBucket.get(dateKey) ?? { units: 0, revenue: 0 };
            entry.units += row.data.quantity || 0;
            entry.revenue += row.data.revenue || 0;
            prevBucket.set(dateKey, entry);
        }
        const prevPoints = Array.from(prevBucket.entries()).sort(([a], [b]) =>
            a.localeCompare(b),
        );

        // Align previous period to current period by day offset
        return currentPoints.map((point, idx) => {
            const prev =
                idx < prevPoints.length ? prevPoints[idx][1] : undefined;
            return {
                ...point,
                prevUnits: prev?.units ?? 0,
                prevRevenue: prev?.revenue ?? 0,
            };
        });
    }, [trendRows, prevTrendRows, showComparison]);

    /* ---------------------------------------------------------------------- */
    /*                        Derived: channel table data                      */
    /* ---------------------------------------------------------------------- */

    const channelTableData = useMemo<ChannelRow[]>(() => {
        const growthMap = matchGrowth(
            channelCardRows,
            channelCardPrevRows,
            (r) => r.grouping?.CHANNEL?.key ?? '',
        );

        // Share is of the card's own total, not the page KPI total — the card can
        // be looking at a different date range.
        const cardRevenue = channelCardRows.reduce(
            (sum, row) => sum + (row.data.revenue || 0),
            0,
        );

        return channelCardRows
            .filter((row) => row.grouping?.CHANNEL?.key)
            .map((row) => {
                const key = row.grouping.CHANNEL!.key;
                const units = row.data.quantity || 0;
                const revenue = row.data.revenue || 0;
                const orders = row.data.orders || 0;
                const entry = growthMap.get(key);
                return {
                    channelId: key,
                    units,
                    revenue,
                    orders,
                    aov: orders > 0 ? revenue / orders : 0,
                    share: cardRevenue > 0 ? (revenue / cardRevenue) * 100 : 0,
                    growth: entry
                        ? computeGrowthLabel(
                              entry.currentRevenue,
                              entry.previousRevenue,
                          )
                        : '—',
                    returnValue: cardReturnsAvailable
                        ? (row.data.returnValue ?? null)
                        : null,
                    returnQuantity: cardReturnsAvailable
                        ? (row.data.returnQuantity ?? null)
                        : null,
                    netValue: cardReturnsAvailable
                        ? (row.data.netValue ?? revenue)
                        : undefined,
                    netQuantity: cardReturnsAvailable
                        ? (row.data.netQuantity ?? units)
                        : undefined,
                };
            })
            .filter((r) => {
                // The card's search box is shared; on this sheet it matches
                // channel names.
                const term = cardSearch.trim().toLowerCase();
                return !term || r.channelId.toLowerCase().includes(term);
            })
            .sort((a, b) => b.revenue - a.revenue);
    }, [channelCardRows, channelCardPrevRows, cardSearch, cardReturnsAvailable]);

    /* ---------------------------------------------------------------------- */
    /*                    Derived: channel mix trend data                      */
    /* ---------------------------------------------------------------------- */

    type ChannelMixMonth = {
        monthLabel: string;
        segments: { channelId: string; share: number; value: number }[];
    };

    const channelMixTrendData = useMemo<ChannelMixMonth[]>(() => {
        const useRevenue = channelMixMode === 'revenue';
        // Group rows by month, then by channel within each month
        const monthMap = new Map<
            string,
            { label: string; order: number; channels: Map<string, number> }
        >();

        for (const row of channelMixRows) {
            const dateKey = normalizeDateKey(row.grouping?.DATE?.key ?? '');
            const channelId = row.grouping?.CHANNEL?.key;
            if (!dateKey || !channelId) continue;

            const d = new Date(dateKey);
            if (Number.isNaN(d.getTime())) continue;

            const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = d.toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
            });

            if (!monthMap.has(monthKey)) {
                monthMap.set(monthKey, {
                    label: monthLabel,
                    order: d.getTime(),
                    channels: new Map(),
                });
            }

            const month = monthMap.get(monthKey)!;
            const rowValue = useRevenue
                ? row.data.revenue || 0
                : row.data.quantity || 0;
            month.channels.set(
                channelId,
                (month.channels.get(channelId) ?? 0) + rowValue,
            );
        }

        // Sort months chronologically and compute shares
        const OTHER_THRESHOLD = 0.05;
        return Array.from(monthMap.values())
            .sort((a, b) => a.order - b.order)
            .map((month) => {
                const totalInMonth = Array.from(month.channels.values()).reduce(
                    (s, v) => s + v,
                    0,
                );

                const segments: {
                    channelId: string;
                    share: number;
                    value: number;
                }[] = [];
                let othersValue = 0;

                // Sort channels by value descending
                const sorted = Array.from(month.channels.entries()).sort(
                    (a, b) => b[1] - a[1],
                );

                for (const [channelId, value] of sorted) {
                    const share = totalInMonth > 0 ? value / totalInMonth : 0;
                    if (share < OTHER_THRESHOLD) {
                        othersValue += value;
                    } else {
                        segments.push({ channelId, share, value });
                    }
                }

                if (othersValue > 0) {
                    segments.push({
                        channelId: 'Others',
                        share:
                            totalInMonth > 0 ? othersValue / totalInMonth : 0,
                        value: othersValue,
                    });
                }

                return { monthLabel: month.label, segments };
            });
    }, [channelMixRows, channelMixMode]);

    // Build a stable color map for channels across months
    const CHANNEL_COLORS = [
        '#6d4bbe',
        '#8468d0',
        '#9b83e0',
        '#b29eea',
        '#c7b8f2',
        '#d9cff7',
        '#e8e0fb',
        '#f0eafd',
        '#f6f3fe',
        '#faf8ff',
    ];

    const channelColorMap = useMemo(() => {
        const map = new Map<string, string>();
        const allChannels = new Set<string>();
        for (const month of channelMixTrendData) {
            for (const seg of month.segments) {
                allChannels.add(seg.channelId);
            }
        }
        let i = 0;
        for (const ch of allChannels) {
            if (ch === 'Others') {
                map.set(ch, '#94a3b8'); // slate-400 for Others
            } else {
                map.set(ch, CHANNEL_COLORS[i % CHANNEL_COLORS.length]);
                i++;
            }
        }
        return map;
    }, [channelMixTrendData]);

    /* ---------------------------------------------------------------------- */
    /*                          Derived: SKU table data                        */
    /* ---------------------------------------------------------------------- */

    const skuTableData = useMemo<SkuRow[]>(() => {
        const growthMapLocal = matchGrowth(
            skuCardRows,
            skuCardPrevRows,
            (r) => r.grouping?.PRODUCT?.key ?? '',
        );

        // The card's Channel and Product chips are applied server-side, so only
        // the free-text search is left to filter here.
        const searchTerm = cardSearch.trim().toLowerCase();

        const rows = skuCardRows
            .filter((row) => row.grouping?.PRODUCT?.key)
            .map((row) => {
                const p = row.grouping.PRODUCT!;
                const entry = growthMapLocal.get(p.key);
                const units = row.data.quantity || 0;
                const revenue = row.data.revenue || 0;
                return {
                    productId: p.key,
                    name: p.name ?? '—',
                    sku: p.sku ?? '—',
                    category: p.category,
                    tier: p.tier,
                    productGroup: p.productGroup,
                    isActive: p.isActive,
                    units,
                    revenue,
                    asp: units > 0 ? revenue / units : 0,
                    mrp: p.price,
                    growth: entry
                        ? computeGrowthLabel(
                              entry.currentRevenue,
                              entry.previousRevenue,
                          )
                        : '—',
                    returnValue: cardReturnsAvailable
                        ? (row.data.returnValue ?? 0)
                        : undefined,
                    returnQuantity: cardReturnsAvailable
                        ? (row.data.returnQuantity ?? 0)
                        : undefined,
                    netValue: cardReturnsAvailable
                        ? (row.data.netValue ?? revenue)
                        : undefined,
                    netQuantity: cardReturnsAvailable
                        ? (row.data.netQuantity ?? units)
                        : undefined,
                };
            })
            .filter((r) => {
                if (!searchTerm) return true;
                const hay = `${r.sku} ${r.name}`.toLowerCase();
                return hay.includes(searchTerm);
            });

        // Sort by units (top selling = most units, slow moving = fewest units)
        if (skuMode === 'top') {
            rows.sort((a, b) => b.units - a.units);
        } else {
            rows.sort((a, b) => a.units - b.units);
        }

        return rows;
    }, [skuCardRows, skuCardPrevRows, skuMode, cardSearch, cardReturnsAvailable]);

    /* Bundle reporting. `showBundles` also needs bundle sales in the range, so a
     * brand switched on with none (or a range without any) sees today's table. */
    const bundleParts = useMemo(
        () => toBundleParts(skuBundleRows),
        [skuBundleRows],
    );
    const bundleRecipes = useMemo(
        () => buildBundleRecipes(bundleParts, bundleEdges),
        [bundleParts, bundleEdges],
    );
    const showBundles =
        bundleReporting.enabled &&
        cardTrendPeriod === 'summary' &&
        hasBundleSales(bundleParts);
    const showAsSold = isSkuTab && showBundles && bundleView === 'asSold';

    const skuDisplayData = useMemo<SkuRow[]>(
        () =>
            showBundles
                ? attachBundleBreakdown(skuTableData, bundleParts, bundleRecipes)
                : skuTableData,
        [showBundles, skuTableData, bundleParts, bundleRecipes],
    );

    const asSoldData = useMemo<AsSoldRow[]>(() => {
        if (!showBundles) return [];
        const searchTerm = cardSearch.trim().toLowerCase();
        const rows = buildAsSoldRows(bundleParts, bundleRecipes).filter(
            (r) =>
                !searchTerm ||
                `${r.sku} ${r.name}`.toLowerCase().includes(searchTerm),
        );
        rows.sort((a, b) =>
            skuMode === 'top'
                ? b.unitsSold - a.unitsSold
                : a.unitsSold - b.unitsSold,
        );
        return rows;
    }, [showBundles, bundleParts, bundleRecipes, cardSearch, skuMode]);

    const skuRowCount = showAsSold ? asSoldData.length : skuDisplayData.length;
    const skuTotalPages = Math.ceil(skuRowCount / skuPageSize);
    const paginatedSkuData = useMemo(
        () =>
            skuDisplayData.slice(
                (skuPage - 1) * skuPageSize,
                skuPage * skuPageSize,
            ),
        [skuDisplayData, skuPage, skuPageSize],
    );
    const paginatedAsSoldData = useMemo(
        () =>
            asSoldData.slice(
                (skuPage - 1) * skuPageSize,
                skuPage * skuPageSize,
            ),
        [asSoldData, skuPage, skuPageSize],
    );

    // Reset SKU page when the card's filters, range, or mode change
    useEffect(() => {
        setSkuPage(1);
    }, [
        skuMode,
        cardChannelFilter,
        cardSearch,
        cardProductFilter,
        cardRange,
        skuPageSize,
    ]);

    /* ---------------------------------------------------------------------- */
    /*                       Fetch: AI Sales Insights                          */
    /* ---------------------------------------------------------------------- */

    /* Both tables come off one card scope, so cardRange always describes both —
     * there is no window for them to disagree about.
     *
     * Skipped while the card is collapsed: this is an LLM round trip, and the
     * collapsed state is persisted, so it used to run on every page load for
     * anyone who had closed the card and would never see the result. */
    useEffect(() => {
        if (
            loading ||
            insightsCollapsed ||
            channelTableData.length === 0 ||
            skuTableData.length === 0
        )
            return;

        const fetchInsights = async () => {
            setInsightsLoading(true);
            try {
                const context = buildInsightsContext({
                    dateRange: cardRange,
                    skuRows: skuTableData,
                    channelRows: channelTableData,
                    totalUnits,
                    totalRevenue,
                    dailyRunRate,
                    dailyRevenueRunRate,
                    revenueGrowth,
                    unitsGrowth,
                });
                const resp = await fetchSalesInsights(context);
                setInsightsData(resp.insights ?? []);
            } catch (err) {
                console.error('Failed to fetch sales insights', err);
                setInsightsData([]);
            } finally {
                setInsightsLoading(false);
            }
        };

        void fetchInsights();
    }, [
        loading,
        insightsCollapsed,
        channelTableData,
        skuTableData,
        cardRange,
        totalUnits,
        totalRevenue,
        dailyRunRate,
        dailyRevenueRunRate,
        revenueGrowth,
        unitsGrowth,
    ]);

    const handleInsightsRefresh = useCallback(() => {
        setInsightsLoading(true);
        const context = buildInsightsContext({
            dateRange: cardRange,
            skuRows: skuTableData,
            channelRows: channelTableData,
            totalUnits,
            totalRevenue,
            dailyRunRate,
            dailyRevenueRunRate,
            revenueGrowth,
            unitsGrowth,
        });
        fetchSalesInsights(context)
            .then((resp) => setInsightsData(resp.insights ?? []))
            .catch((err) => {
                console.error('Failed to refresh sales insights', err);
                setInsightsData([]);
            })
            .finally(() => setInsightsLoading(false));
    }, [
        cardRange,
        skuTableData,
        channelTableData,
        totalUnits,
        totalRevenue,
        dailyRunRate,
        dailyRevenueRunRate,
        revenueGrowth,
        unitsGrowth,
    ]);

    /* ---------------------------------------------------------------------- */
    /*                        Derived: pane breakdown                          */
    /* ---------------------------------------------------------------------- */

    const paneChannelUnits = useMemo(
        () =>
            paneProductRows.reduce((sum, r) => sum + (r.data.quantity || 0), 0),
        [paneProductRows],
    );

    const paneChannelRevenue = useMemo(
        () =>
            paneProductRows.reduce((sum, r) => sum + (r.data.revenue || 0), 0),
        [paneProductRows],
    );

    const paneChannelReturnUnits = useMemo(
        () =>
            paneProductRows.reduce(
                (sum, r) => sum + (r.data.returnQuantity ?? 0),
                0,
            ),
        [paneProductRows],
    );

    const paneChannelReturnValue = useMemo(
        () =>
            paneProductRows.reduce(
                (sum, r) => sum + (r.data.returnValue ?? 0),
                0,
            ),
        [paneProductRows],
    );

    const paneBreakdownRows = useMemo<BreakdownRow[]>(() => {
        const growthMap = matchGrowth(
            paneProductRows,
            panePrevProductRows,
            (r) => r.grouping?.PRODUCT?.key ?? '',
        );

        if (paneGroupBy === 'product') {
            return paneProductRows
                .filter((r) => r.grouping?.PRODUCT?.key)
                .map((r) => {
                    const p = r.grouping.PRODUCT!;
                    const entry = growthMap.get(p.key);
                    const units = r.data.quantity || 0;
                    const revenue = r.data.revenue || 0;
                    return {
                        key: p.key,
                        label: `${p.name ?? '—'} (${p.sku ?? '—'})`,
                        revenue,
                        units,
                        share:
                            paneChannelRevenue > 0
                                ? (revenue / paneChannelRevenue) * 100
                                : 0,
                        growth: entry
                            ? computeGrowthLabel(
                                  entry.currentRevenue,
                                  entry.previousRevenue,
                              )
                            : '—',
                        returnUnits: returnsAvailable
                            ? (r.data.returnQuantity ?? 0)
                            : undefined,
                        netUnits: returnsAvailable
                            ? (r.data.netQuantity ?? units)
                            : undefined,
                    };
                })
                .sort((a, b) => b.revenue - a.revenue);
        }

        // Group by category / productGroup / tier
        const fieldKey =
            paneGroupBy === 'category'
                ? 'category'
                : paneGroupBy === 'productGroup'
                  ? 'productGroup'
                  : 'tier';

        const grouped = new Map<
            string,
            {
                units: number;
                revenue: number;
                currentRevenue: number;
                prevRevenue: number;
                returnUnits: number;
            }
        >();
        for (const r of paneProductRows) {
            const val = r.grouping?.PRODUCT?.[fieldKey] ?? 'Unknown';
            const entry = grouped.get(val) ?? {
                units: 0,
                revenue: 0,
                currentRevenue: 0,
                prevRevenue: 0,
                returnUnits: 0,
            };
            entry.units += r.data.quantity || 0;
            entry.revenue += r.data.revenue || 0;
            entry.currentRevenue += r.data.revenue || 0;
            entry.returnUnits += r.data.returnQuantity ?? 0;
            grouped.set(val, entry);
        }
        for (const r of panePrevProductRows) {
            const val = r.grouping?.PRODUCT?.[fieldKey] ?? 'Unknown';
            const entry = grouped.get(val) ?? {
                units: 0,
                revenue: 0,
                currentRevenue: 0,
                prevRevenue: 0,
                returnUnits: 0,
            };
            entry.prevRevenue += r.data.revenue || 0;
            grouped.set(val, entry);
        }

        return Array.from(grouped.entries())
            .map(([label, entry]) => ({
                key: label,
                label,
                revenue: entry.revenue,
                units: entry.units,
                share:
                    paneChannelRevenue > 0
                        ? (entry.revenue / paneChannelRevenue) * 100
                        : 0,
                growth: computeGrowthLabel(
                    entry.currentRevenue,
                    entry.prevRevenue,
                ),
                returnUnits: returnsAvailable ? entry.returnUnits : undefined,
                netUnits: returnsAvailable
                    ? entry.units - entry.returnUnits
                    : undefined,
            }))
            .sort((a, b) => b.revenue - a.revenue);
    }, [
        paneProductRows,
        panePrevProductRows,
        paneGroupBy,
        paneChannelRevenue,
        returnsAvailable,
    ]);

    const paneTrendData = useMemo<SalesChartPoint[]>(() => {
        const bucket = new Map<string, { units: number; revenue: number }>();
        for (const row of paneTrendRows) {
            const dateKey = normalizeDateKey(row.grouping?.DATE?.key ?? '');
            if (!dateKey) continue;
            const entry = bucket.get(dateKey) ?? { units: 0, revenue: 0 };
            entry.units += row.data.quantity || 0;
            entry.revenue += row.data.revenue || 0;
            bucket.set(dateKey, entry);
        }
        return Array.from(bucket.entries())
            .map(([date, vals]) => ({
                date,
                units: vals.units,
                revenue: vals.revenue,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [paneTrendRows]);

    const paneGrowthLabel = useMemo(() => {
        const prevTotal = panePrevProductRows.reduce(
            (sum, r) => sum + (r.data.quantity || 0),
            0,
        );
        return computeGrowthLabel(paneChannelUnits, prevTotal);
    }, [paneChannelUnits, panePrevProductRows]);

    const paneShareLabel = useMemo(() => {
        if (totalUnits <= 0) return '—';
        return `${((paneChannelUnits / totalUnits) * 100).toFixed(1)}%`;
    }, [paneChannelUnits, totalUnits]);

    /* ---------------------------------------------------------------------- */
    /*                        Derived: SKU pane data                           */
    /* ---------------------------------------------------------------------- */

    // Look up the clicked SKU's metadata from the product rows
    const selectedSkuMeta = useMemo(() => {
        if (!selectedSkuId) return null;
        const row = skuCardRows.find(
            (r) => r.grouping?.PRODUCT?.key === selectedSkuId,
        );
        if (!row?.grouping?.PRODUCT) return null;
        const p = row.grouping.PRODUCT;
        return { name: p.name ?? '—', sku: p.sku ?? '—' };
    }, [selectedSkuId, skuCardRows]);

    const skuPaneTotalUnits = useMemo(
        () =>
            skuPaneChannelRows.reduce(
                (sum, r) => sum + (r.data.quantity || 0),
                0,
            ),
        [skuPaneChannelRows],
    );

    const skuPaneTotalRevenue = useMemo(
        () =>
            skuPaneChannelRows.reduce(
                (sum, r) => sum + (r.data.revenue || 0),
                0,
            ),
        [skuPaneChannelRows],
    );

    const skuPaneReturnUnits = useMemo(
        () =>
            skuPaneChannelRows.reduce(
                (sum, r) => sum + (r.data.returnQuantity ?? 0),
                0,
            ),
        [skuPaneChannelRows],
    );

    const skuPaneReturnValue = useMemo(
        () =>
            skuPaneChannelRows.reduce(
                (sum, r) => sum + (r.data.returnValue ?? 0),
                0,
            ),
        [skuPaneChannelRows],
    );

    const skuPaneGrowthLabel = useMemo(() => {
        const prevTotal = skuPanePrevChannelRows.reduce(
            (sum, r) => sum + (r.data.quantity || 0),
            0,
        );
        return computeGrowthLabel(skuPaneTotalUnits, prevTotal);
    }, [skuPaneTotalUnits, skuPanePrevChannelRows]);

    const skuPaneShareLabel = useMemo(() => {
        if (totalUnits <= 0) return '—';
        return `${((skuPaneTotalUnits / totalUnits) * 100).toFixed(1)}%`;
    }, [skuPaneTotalUnits, totalUnits]);

    const skuPaneBreakdownRows = useMemo<ChannelRow[]>(() => {
        const growthMap = matchGrowth(
            skuPaneChannelRows,
            skuPanePrevChannelRows,
            (r) => r.grouping?.CHANNEL?.key ?? '',
        );

        return skuPaneChannelRows
            .filter((r) => r.grouping?.CHANNEL?.key)
            .map((r) => {
                const key = r.grouping.CHANNEL!.key;
                const units = r.data.quantity || 0;
                const revenue = r.data.revenue || 0;
                const orders = r.data.orders || 0;
                const entry = growthMap.get(key);
                return {
                    channelId: key,
                    units,
                    revenue,
                    orders,
                    aov: orders > 0 ? revenue / orders : 0,
                    share:
                        skuPaneTotalRevenue > 0
                            ? (revenue / skuPaneTotalRevenue) * 100
                            : 0,
                    growth: entry
                        ? computeGrowthLabel(
                              entry.currentRevenue,
                              entry.previousRevenue,
                          )
                        : '—',
                    returnValue: returnsAvailable
                        ? (r.data.returnValue ?? null)
                        : null,
                    returnQuantity: returnsAvailable
                        ? (r.data.returnQuantity ?? null)
                        : null,
                    netValue: returnsAvailable
                        ? (r.data.netValue ?? revenue)
                        : undefined,
                    netQuantity: returnsAvailable
                        ? (r.data.netQuantity ?? units)
                        : undefined,
                };
            })
            .sort((a, b) => b.revenue - a.revenue);
    }, [
        skuPaneChannelRows,
        skuPanePrevChannelRows,
        skuPaneTotalRevenue,
        returnsAvailable,
    ]);

    /* ---------------------------------------------------------------------- */
    /*                            CSV export                                   */
    /* ---------------------------------------------------------------------- */

    const handleExportCsv = useCallback(() => {
        const revenueHeader = revenueColumnHeader(revenueMode);
        const header = [
            'Channel',
            revenueHeader,
            'Orders',
            'Units',
            'Share %',
            'Growth %',
        ];
        const lines = channelTableData.map((r) =>
            [
                r.channelId,
                r.revenue.toFixed(2),
                r.orders,
                r.units,
                r.share.toFixed(1),
                r.growth,
            ].join(','),
        );
        const csv = [header.join(','), ...lines].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales_${range.startDate}_${range.endDate}_${revenueMode}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [channelTableData, range, revenueMode]);

    const handlePaneExportCsv = useCallback(() => {
        const revenueHeader = revenueColumnHeader(revenueMode);
        const header = ['Name', revenueHeader, 'Units', 'Share %', 'Growth %'];
        const lines = paneBreakdownRows.map((r) =>
            [
                r.label,
                r.revenue.toFixed(2),
                r.units,
                r.share.toFixed(1),
                r.growth,
            ].join(','),
        );
        const csv = [header.join(','), ...lines].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales_${selectedChannelId}_${range.startDate}_${range.endDate}_${revenueMode}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [paneBreakdownRows, selectedChannelId, range, revenueMode]);

    /* ---------------------------------------------------------------------- */
    /*                         Pane open / close                               */
    /* ---------------------------------------------------------------------- */

    const openChannelPane = useCallback(
        (row: ChannelRow) => {
            setSearchParams({ channelId: row.channelId });
        },
        [setSearchParams],
    );

    const closeChannelPane = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('channelId');
            return next;
        });
    }, [setSearchParams]);

    const openSkuPane = useCallback(
        (row: SkuRow) => {
            setSearchParams({ skuId: row.productId });
        },
        [setSearchParams],
    );

    const closeSkuPane = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('skuId');
            return next;
        });
    }, [setSearchParams]);

    const handleSkuPaneExportCsv = useCallback(() => {
        const revenueHeader = revenueColumnHeader(revenueMode);
        const header = [
            'Channel',
            revenueHeader,
            'Orders',
            'Units',
            'Share %',
            'Growth %',
        ];
        const lines = skuPaneBreakdownRows.map((r) =>
            [
                r.channelId,
                r.revenue.toFixed(2),
                r.orders,
                r.units,
                r.share.toFixed(1),
                r.growth,
            ].join(','),
        );
        const csv = [header.join(','), ...lines].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales_sku_${selectedSkuId}_${range.startDate}_${range.endDate}_${revenueMode}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [skuPaneBreakdownRows, selectedSkuId, range, revenueMode]);

    /* ---------------------------------------------------------------------- */
    /*                          Filter configs                                 */
    /* ---------------------------------------------------------------------- */

    const cardChannelFilterValue = useMemo<FilterValue>(
        () => ({ channel: cardChannelFilter }),
        [cardChannelFilter],
    );

    const cardChannelFilterConfig: DataSheetFilterConfig = useMemo(
        () => ({
            label: 'Channel',
            params: [
                {
                    key: 'channel',
                    label: 'Channel',
                    mode: 'multi',
                    options: channelOptions,
                },
            ],
            value: cardChannelFilterValue,
            onChange: (val: FilterValue) => {
                setCardChannelFilter((val.channel as string[]) ?? []);
            },
        }),
        [channelOptions, cardChannelFilterValue],
    );

    const cardProductFilterConfig: DataSheetFilterConfig = useMemo(
        () => ({
            label: 'Product',
            params: cardProductFilterParams,
            value: cardProductFilter,
            onChange: setCardProductFilter,
        }),
        [cardProductFilterParams, cardProductFilter, setCardProductFilter],
    );

    /* ---------------------------------------------------------------------- */
    /*                            Column definitions                           */
    /* ---------------------------------------------------------------------- */

    const channelColumns = useMemo(() => {
        const hidden = new Set(channelHiddenCols);
        return buildChannelColumns(revenueMode).filter(
            (c) => !hidden.has(c.key),
        );
    }, [revenueMode, channelHiddenCols]);
    const skuColumns = useMemo(() => {
        const hidden = new Set(skuHiddenCols);
        const columns = buildSkuColumns(
            (skuPage - 1) * skuPageSize,
            revenueMode,
        ).filter((c) => !hidden.has(c.key));
        if (!showBundles) return columns;
        // By product: revenue is the allocated share, and two bundle columns follow Units.
        const withHeader = columns.map((c) =>
            c.key === 'revenue'
                ? { ...c, renderHeader: allocatedRevenueHeader }
                : c,
        );
        const at = withHeader.findIndex((c) => c.key === 'units') + 1;
        return [
            ...withHeader.slice(0, at),
            ...buildBundleShareColumns(),
            ...withHeader.slice(at),
        ];
    }, [skuPage, skuPageSize, revenueMode, skuHiddenCols, showBundles]);
    const asSoldColumns = useMemo(
        () => buildAsSoldColumns((skuPage - 1) * skuPageSize, formatCurrency),
        [skuPage, skuPageSize],
    );
    const breakdownColumns = useMemo(
        () => buildBreakdownColumns(revenueMode),
        [revenueMode],
    );

    const channelPeriodColumns = useMemo(() => {
        if (cardTrendPeriod === 'summary') return channelColumns;
        const pType = cardTrendPeriod === 'weekly' ? 'weekly' : 'monthly';
        const periods = extractPeriods(channelPeriodRows, pType);
        const periodData = bucketByEntityAndPeriod(
            channelPeriodRows,
            'CHANNEL',
            pType,
        );
        return buildChannelPeriodColumns(
            periods,
            periodData,
            revenueMode,
            returnsAvailableFrom,
        );
    }, [
        cardTrendPeriod,
        channelPeriodRows,
        channelColumns,
        revenueMode,
        returnsAvailableFrom,
    ]);

    const channelDownloadColumns = useMemo<
        DataSheetColumn<ChannelRow>[]
    >(() => {
        const revenueHeader = revenueColumnHeader(revenueMode);
        const baseCols: DataSheetColumn<ChannelRow>[] = [
            {
                key: 'channelId',
                header: 'Channel',
                csvValue: (_v, r) => r.channelId ?? '',
            },
        ];
        if (cardTrendPeriod === 'summary') {
            return [
                ...baseCols,
                {
                    key: 'units',
                    header: 'Units',
                    csvValue: (_v, r) => r.units ?? 0,
                },
                {
                    key: 'returnQuantity',
                    header: 'Return Units',
                    csvValue: (_v, r) => r.returnQuantity ?? 0,
                },
                {
                    key: 'netQuantity',
                    header: 'Net Units',
                    csvValue: (_v, r) => r.netQuantity ?? 0,
                },
                {
                    key: 'revenue',
                    header: revenueHeader,
                    csvValue: (_v, r) => r.revenue ?? 0,
                },
                {
                    key: 'returnValue',
                    header: 'Return Value',
                    csvValue: (_v, r) => r.returnValue ?? 0,
                },
                {
                    key: 'netValue',
                    header: 'Net Value',
                    csvValue: (_v, r) => r.netValue ?? 0,
                },
                {
                    key: 'orders',
                    header: 'Orders',
                    csvValue: (_v, r) => r.orders ?? 0,
                },
                {
                    key: 'share',
                    header: 'Share %',
                    csvValue: (_v, r) => (r.share ?? 0).toFixed(1),
                },
                {
                    key: 'growth',
                    header: 'Growth',
                    csvValue: (_v, r) => r.growth ?? '',
                },
            ];
        }
        const pType = cardTrendPeriod === 'weekly' ? 'weekly' : 'monthly';
        const periods = extractPeriods(channelPeriodRows, pType);
        const periodData = bucketByEntityAndPeriod(
            channelPeriodRows,
            'CHANNEL',
            pType,
        );
        const cols: DataSheetColumn<ChannelRow>[] = [...baseCols];
        for (const p of periods) {
            cols.push({
                key: `period_${p.key}_revenue_csv`,
                header: `${p.label} ${revenueHeader}`,
                csvValue: (_v, r) =>
                    periodData.get(r.channelId)?.get(p.key)?.revenue ?? 0,
            });
            cols.push({
                key: `period_${p.key}_units_csv`,
                header: `${p.label} Units`,
                csvValue: (_v, r) =>
                    periodData.get(r.channelId)?.get(p.key)?.units ?? 0,
            });
            cols.push({
                key: `period_${p.key}_return_units_csv`,
                header: `${p.label} Return Units`,
                csvValue: (_v, r) =>
                    periodHasReturns(p, returnsAvailableFrom)
                        ? (periodData.get(r.channelId)?.get(p.key)
                              ?.returnUnits ?? 0)
                        : '',
            });
        }
        return cols;
    }, [
        cardTrendPeriod,
        channelPeriodRows,
        revenueMode,
        returnsAvailableFrom,
    ]);

    const skuPeriodColumns = useMemo(() => {
        if (cardTrendPeriod === 'summary') return skuColumns;
        const pType = cardTrendPeriod === 'weekly' ? 'weekly' : 'monthly';
        const periods = extractPeriods(skuPeriodRows, pType);
        const periodData = bucketByEntityAndPeriod(
            skuPeriodRows,
            'PRODUCT',
            pType,
        );
        return buildSkuPeriodColumns(
            periods,
            periodData,
            (skuPage - 1) * skuPageSize,
            revenueMode,
            returnsAvailableFrom,
        );
    }, [
        cardTrendPeriod,
        skuPeriodRows,
        skuColumns,
        skuPage,
        skuPageSize,
        revenueMode,
        returnsAvailableFrom,
    ]);

    const channelAggregateRow = useMemo<Record<string, string>>(() => {
        const result: Record<string, string> = { channelId: 'Total' };

        if (cardTrendPeriod === 'summary') {
            const revenueSum = channelTableData.reduce(
                (s, r) => s + (r.revenue || 0),
                0,
            );
            const ordersSum = channelTableData.reduce(
                (s, r) => s + (r.orders || 0),
                0,
            );
            const unitsSum = channelTableData.reduce(
                (s, r) => s + (r.units || 0),
                0,
            );
            const returnSum = channelTableData.reduce(
                (s, r) => s + (r.returnValue ?? 0),
                0,
            );
            const returnUnitsSum = channelTableData.reduce(
                (s, r) => s + (r.returnQuantity ?? 0),
                0,
            );
            result.revenue = formatCurrency(revenueSum);
            result.orders = formatCount(ordersSum);
            result.units = formatCount(unitsSum);
            result.returnValue = cardReturnsAvailable
                ? formatCurrency(returnSum)
                : '—';
            result.netValue = cardReturnsAvailable
                ? formatCurrency(revenueSum - returnSum)
                : '—';
            result.returnQuantity = cardReturnsAvailable
                ? formatCount(returnUnitsSum)
                : '—';
            result.netQuantity = cardReturnsAvailable
                ? formatCount((unitsSum - returnUnitsSum))
                : '—';
            return result;
        }

        const pType = cardTrendPeriod === 'weekly' ? 'weekly' : 'monthly';
        const periods = extractPeriods(channelPeriodRows, pType);
        const periodData = bucketByEntityAndPeriod(
            channelPeriodRows,
            'CHANNEL',
            pType,
        );
        const visibleIds = new Set(channelTableData.map((r) => r.channelId));

        for (const p of periods) {
            let rev = 0;
            let units = 0;
            let returnUnits = 0;
            for (const cid of visibleIds) {
                const entry = periodData.get(cid)?.get(p.key);
                if (entry) {
                    rev += entry.revenue;
                    units += entry.units;
                    returnUnits += entry.returnUnits;
                }
            }
            result[`period_${p.key}_revenue`] = formatCurrency(rev);
            result[`period_${p.key}_units`] = formatCount(units);
            result[`period_${p.key}_returnUnits`] = periodHasReturns(
                p,
                returnsAvailableFrom,
            )
                ? formatCount(returnUnits)
                : '—';
        }
        return result;
    }, [
        channelTableData,
        cardTrendPeriod,
        channelPeriodRows,
        cardReturnsAvailable,
        returnsAvailableFrom,
    ]);

    const skuAggregateRow = useMemo<Record<string, string>>(() => {
        const result: Record<string, string> = { name: 'Total' };

        if (cardTrendPeriod === 'summary') {
            const revenueSum = skuTableData.reduce(
                (s, r) => s + (r.revenue || 0),
                0,
            );
            const unitsSum = skuTableData.reduce(
                (s, r) => s + (r.units || 0),
                0,
            );
            const returnSum = skuTableData.reduce(
                (s, r) => s + (r.returnValue ?? 0),
                0,
            );
            const returnUnitsSum = skuTableData.reduce(
                (s, r) => s + (r.returnQuantity ?? 0),
                0,
            );
            result.revenue = formatCurrency(revenueSum);
            result.units = formatCount(unitsSum);
            result.returnValue = cardReturnsAvailable
                ? formatCurrency(returnSum)
                : '—';
            result.netValue = cardReturnsAvailable
                ? formatCurrency(revenueSum - returnSum)
                : '—';
            result.returnQuantity = cardReturnsAvailable
                ? formatCount(returnUnitsSum)
                : '—';
            result.netQuantity = cardReturnsAvailable
                ? formatCount((unitsSum - returnUnitsSum))
                : '—';
            return result;
        }

        const pType = cardTrendPeriod === 'weekly' ? 'weekly' : 'monthly';
        const periods = extractPeriods(skuPeriodRows, pType);
        const periodData = bucketByEntityAndPeriod(
            skuPeriodRows,
            'PRODUCT',
            pType,
        );
        const visibleIds = new Set(skuTableData.map((r) => r.productId));

        for (const p of periods) {
            let rev = 0;
            let units = 0;
            let returnUnits = 0;
            for (const pid of visibleIds) {
                const entry = periodData.get(pid)?.get(p.key);
                if (entry) {
                    rev += entry.revenue;
                    units += entry.units;
                    returnUnits += entry.returnUnits;
                }
            }
            result[`period_${p.key}_revenue`] = formatCurrency(rev);
            result[`period_${p.key}_units`] = formatCount(units);
            result[`period_${p.key}_returnUnits`] = periodHasReturns(
                p,
                returnsAvailableFrom,
            )
                ? formatCount(returnUnits)
                : '—';
        }
        return result;
    }, [
        skuTableData,
        cardTrendPeriod,
        skuPeriodRows,
        cardReturnsAvailable,
        returnsAvailableFrom,
    ]);

    const asSoldAggregateRow = useMemo<Record<string, string>>(
        () => ({
            name: 'Total',
            unitsSold: formatCount(
                asSoldData.reduce((sum, r) => sum + r.unitsSold, 0),
            ),
            revenue: formatCurrency(
                asSoldData.reduce((sum, r) => sum + r.revenue, 0),
            ),
        }),
        [asSoldData],
    );

    const skuDownloadColumns = useMemo<DataSheetColumn<SkuRow>[]>(() => {
        const revenueHeader = revenueColumnHeader(revenueMode);
        const baseCols: DataSheetColumn<SkuRow>[] = [
            { key: 'name', header: 'SKU', csvValue: (_v, r) => r.name ?? '' },
            {
                key: 'sku',
                header: 'SKU Code',
                csvValue: (_v, r) => r.sku ?? '',
            },
        ];
        if (cardTrendPeriod === 'summary') {
            return [
                ...baseCols,
                {
                    key: 'units',
                    header: 'Units',
                    csvValue: (_v, r) => r.units ?? 0,
                },
                {
                    key: 'returnQuantity',
                    header: 'Return Units',
                    csvValue: (_v, r) => r.returnQuantity ?? 0,
                },
                {
                    key: 'netQuantity',
                    header: 'Net Units',
                    csvValue: (_v, r) => r.netQuantity ?? 0,
                },
                {
                    key: 'revenue',
                    header: revenueHeader,
                    csvValue: (_v, r) => r.revenue ?? 0,
                },
                {
                    key: 'returnValue',
                    header: 'Return Value',
                    csvValue: (_v, r) => r.returnValue ?? 0,
                },
                {
                    key: 'netValue',
                    header: 'Net Value',
                    csvValue: (_v, r) => r.netValue ?? 0,
                },
                { key: 'asp', header: 'ASP', csvValue: (_v, r) => r.asp ?? 0 },
                { key: 'mrp', header: 'MRP', csvValue: (_v, r) => r.mrp ?? '' },
                {
                    key: 'growth',
                    header: 'Growth',
                    csvValue: (_v, r) => r.growth ?? '',
                },
            ];
        }
        const pType = cardTrendPeriod === 'weekly' ? 'weekly' : 'monthly';
        const periods = extractPeriods(skuPeriodRows, pType);
        const periodData = bucketByEntityAndPeriod(
            skuPeriodRows,
            'PRODUCT',
            pType,
        );
        const cols: DataSheetColumn<SkuRow>[] = [...baseCols];
        for (const p of periods) {
            cols.push({
                key: `period_${p.key}_revenue_csv`,
                header: `${p.label} ${revenueHeader}`,
                csvValue: (_v, r) =>
                    periodData.get(r.productId)?.get(p.key)?.revenue ?? 0,
            });
            cols.push({
                key: `period_${p.key}_units_csv`,
                header: `${p.label} Units`,
                csvValue: (_v, r) =>
                    periodData.get(r.productId)?.get(p.key)?.units ?? 0,
            });
            cols.push({
                key: `period_${p.key}_return_units_csv`,
                header: `${p.label} Return Units`,
                csvValue: (_v, r) =>
                    periodHasReturns(p, returnsAvailableFrom)
                        ? (periodData.get(r.productId)?.get(p.key)
                              ?.returnUnits ?? 0)
                        : '',
            });
        }
        return cols;
    }, [
        cardTrendPeriod,
        skuPeriodRows,
        revenueMode,
        returnsAvailableFrom,
    ]);

    /* ---------------------------------------------------------------------- */
    /*                                 Render                                  */
    /* ---------------------------------------------------------------------- */

    return (
        <div className="flex h-full w-full flex-col overflow-y-auto page-shell">
            <PageHeader
                icon="💰"
                title="Sales"
                subtitle="Product sales performance"
                actions={
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleExportCsv}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 500,
                                color: 'rgb(100, 116, 139)',
                                backgroundColor: 'transparent',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: 6,
                                cursor: 'pointer',
                            }}
                        >
                            <DownloadSimple size={14} />
                            Export
                        </button>
                        <AskAiButton />
                    </div>
                }
            />

            {/* Filter bar */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 20,
                    position: 'relative',
                }}
            >
                <button
                    type="button"
                    onClick={() => setShowCalendar(true)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'rgb(26, 32, 44)',
                        backgroundColor: '#fff',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: 8,
                        cursor: 'pointer',
                    }}
                >
                    📅 {formatDisplayRange(range)}
                </button>
                {showCalendar && (
                    <DatePopover
                        range={range}
                        onApply={(r) => {
                            setRange(r);
                            setShowCalendar(false);
                        }}
                        onClose={() => setShowCalendar(false)}
                    />
                )}
                <EntityFilterDropdown
                    label="Channel"
                    params={[
                        {
                            key: 'channel',
                            label: 'Channel',
                            mode: 'multi',
                            options: channelOptions,
                        },
                    ]}
                    value={channelFilter}
                    onChange={setChannelFilter}
                />
                <EntityFilterDropdown
                    label="Product"
                    params={productFilterParams}
                    value={productFilter}
                    onChange={setProductFilter}
                />
            </div>

            {/* Revenue mode badge */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                }}
            >
                <span className="text-xs font-medium text-slate-500">
                    Revenue:
                </span>
                <span
                    className={
                        revenueMode === 'catalog'
                            ? 'inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200'
                            : 'inline-flex items-center rounded-full bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200'
                    }
                    title={
                        revenueMode === 'catalog'
                            ? 'Catalog mode: revenue is calculated as units sold × list price from your products catalog. Switch to Reported mode (internal) to use actuals from sales data.'
                            : 'Reported mode: revenue is the sum of actual amounts received from your sales data feeds.'
                    }
                >
                    {revenueMode === 'catalog'
                        ? 'Catalog (list price)'
                        : 'Reported'}
                </span>
            </div>

            {/* KPI cards */}
            <div
                style={{
                    display: 'flex',
                    gap: 16,
                    marginBottom: 24,
                    flexWrap: 'wrap',
                }}
            >
                <KpiCard
                    label={
                        revenueMode === 'catalog'
                            ? 'Total Revenue (Catalog)'
                            : 'Total Revenue'
                    }
                    value={formatCurrency(totalRevenue)}
                    trendValue={revenueGrowth}
                    trendDirection={trendDirection(revenueGrowth)}
                    helperText="vs last period"
                />
                <KpiCard
                    label="Returns"
                    value={
                        returnsAvailable
                            ? formatCurrency(totalReturnValue)
                            : '—'
                    }
                    helperText={
                        returnsAvailable
                            ? `${formatUnits(totalReturnUnits)} units`
                            : `From ${returnsFromLabel}`
                    }
                />
                <KpiCard
                    label={
                        revenueMode === 'catalog'
                            ? 'Net Sales (Catalog)'
                            : 'Net Sales'
                    }
                    value={
                        returnsAvailable ? formatCurrency(totalNetValue) : '—'
                    }
                    helperText={
                        returnsAvailable
                            ? `${formatUnits(totalNetUnits)} units`
                            : `Returns from ${returnsFromLabel}`
                    }
                />
                <KpiCard
                    label="Sales Growth"
                    value={revenueGrowth}
                    trendDirection={trendDirection(revenueGrowth)}
                    helperText="Revenue, period over period"
                />
                <KpiCard
                    label="Units Sold"
                    value={formatUnits(totalUnits)}
                    trendValue={unitsGrowth}
                    trendDirection={trendDirection(unitsGrowth)}
                    helperText="vs last period"
                />
                <KpiCard
                    label="AOV (B2C)"
                    value={
                        b2cAov > 0 ? formatCurrency(Math.round(b2cAov)) : '—'
                    }
                    trendValue={aovGrowth}
                    trendDirection={trendDirection(aovGrowth)}
                    helperText="B2C Avg Order Value"
                />
            </div>

            {!returnsAvailable && returnsAvailableFrom && (
                <div
                    style={{
                        margin: '-8px 0 16px',
                        fontSize: 13,
                        color: 'rgb(100, 116, 139)',
                    }}
                >
                    Returns and net sales are available from {returnsFromLabel}.
                    Narrow the date range to view them.
                </div>
            )}

            {/* AI Sales Insights */}
            <SalesInsightsCard
                insights={insightsData}
                loading={insightsLoading}
                collapsed={insightsCollapsed}
                onToggleCollapse={() =>
                    setInsightsCollapsed(!insightsCollapsed)
                }
                onRefresh={handleInsightsRefresh}
            />

            {/* Sales Trend chart */}
            <div style={{ marginBottom: 24 }}>
                <GenericChartCard<SalesChartPoint>
                    kind="line"
                    title="Sales Trend"
                    subtitle={
                        showComparison
                            ? `${formatDisplayRange(range)} vs ${formatDisplayRange(buildPreviousRange(range))}`
                            : `Showing ${formatDisplayRange(range)}`
                    }
                    data={chartData}
                    dimensionKey="date"
                    dimensionType="date"
                    series={
                        trendMode === 'revenue'
                            ? [
                                  {
                                      id: 'revenue',
                                      key: 'revenue' as keyof SalesChartPoint,
                                      label: showComparison
                                          ? 'Current Period'
                                          : 'Revenue',
                                      color: '#2563eb',
                                      renderAs: 'line',
                                  },
                                  ...(returnsAvailable
                                      ? [
                                            {
                                                id: 'returnRevenue',
                                                key: 'returnRevenue' as keyof SalesChartPoint,
                                                label: 'Returns',
                                                color: '#ef4444',
                                                renderAs: 'line' as const,
                                            },
                                            {
                                                id: 'netRevenue',
                                                key: 'netRevenue' as keyof SalesChartPoint,
                                                label: 'Net',
                                                color: '#059669',
                                                renderAs: 'line' as const,
                                            },
                                        ]
                                      : []),
                                  ...(showComparison
                                      ? [
                                            {
                                                id: 'prevRevenue',
                                                key: 'prevRevenue' as keyof SalesChartPoint,
                                                label: 'Previous Period',
                                                color: '#93c5fd',
                                                renderAs: 'line' as const,
                                                variant: 'dashed' as const,
                                            },
                                        ]
                                      : []),
                              ]
                            : [
                                  {
                                      id: 'units',
                                      key: 'units' as keyof SalesChartPoint,
                                      label: showComparison
                                          ? 'Current Period'
                                          : 'Units',
                                      color: '#2563eb',
                                      renderAs: 'line',
                                  },
                                  ...(returnsAvailable
                                      ? [
                                            {
                                                id: 'returnUnits',
                                                key: 'returnUnits' as keyof SalesChartPoint,
                                                label: 'Returns',
                                                color: '#ef4444',
                                                renderAs: 'line' as const,
                                            },
                                            {
                                                id: 'netUnits',
                                                key: 'netUnits' as keyof SalesChartPoint,
                                                label: 'Net',
                                                color: '#059669',
                                                renderAs: 'line' as const,
                                            },
                                        ]
                                      : []),
                                  ...(showComparison
                                      ? [
                                            {
                                                id: 'prevUnits',
                                                key: 'prevUnits' as keyof SalesChartPoint,
                                                label: 'Previous Period',
                                                color: '#93c5fd',
                                                renderAs: 'line' as const,
                                                variant: 'dashed' as const,
                                            },
                                        ]
                                      : []),
                              ]
                    }
                    xLabel="Date"
                    yLabel={
                        trendMode === 'revenue' ? 'Revenue (\u20B9)' : 'Units'
                    }
                    height={280}
                    controls={{
                        showToolbar: true,
                        showRange: false,
                        showSeriesToggles: showComparison,
                    }}
                    loading={loading}
                    onAddToChat={addToChat}
                    rightSlot={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() =>
                                    setShowComparison((prev) => !prev)
                                }
                                style={{
                                    padding: '4px 10px',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: showComparison
                                        ? '#2563eb'
                                        : 'rgb(100, 116, 139)',
                                    backgroundColor: showComparison
                                        ? 'rgba(37, 99, 235, 0.08)'
                                        : 'transparent',
                                    border: `1px solid ${showComparison ? 'rgba(37, 99, 235, 0.3)' : 'rgba(0,0,0,0.1)'}`,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Compare
                            </button>
                            <SegmentedToggle
                                value={trendMode}
                                onChange={(val) =>
                                    setTrendMode(val as 'revenue' | 'units')
                                }
                                options={[
                                    { label: 'Revenue', value: 'revenue' },
                                    { label: 'Units', value: 'units' },
                                ]}
                            />
                        </div>
                    }
                />
            </div>

            {/* Channel Mix Trend */}
            <div
                style={{
                    marginBottom: 24,
                    padding: 20,
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 16,
                    }}
                >
                    <h3
                        style={{
                            margin: 0,
                            fontSize: 15,
                            fontWeight: 600,
                            color: 'rgb(26, 32, 44)',
                        }}
                    >
                        Channel Mix Trend
                    </h3>
                    <SegmentedToggle
                        value={channelMixMode}
                        onChange={(val) =>
                            setChannelMixMode(val as 'units' | 'revenue')
                        }
                        options={[
                            { label: 'Revenue', value: 'revenue' },
                            { label: 'Units', value: 'units' },
                        ]}
                    />
                </div>

                {channelMixTrendData.length === 0 && !loading && (
                    <div
                        style={{
                            padding: '24px 0',
                            textAlign: 'center',
                            color: 'rgb(100, 116, 139)',
                            fontSize: 13,
                        }}
                    >
                        No channel mix data available.
                    </div>
                )}

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                    }}
                >
                    {channelMixTrendData.map((month) => (
                        <div
                            key={month.monthLabel}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <span
                                style={{
                                    width: 70,
                                    flexShrink: 0,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: 'rgb(71, 85, 105)',
                                    textAlign: 'right',
                                }}
                            >
                                {month.monthLabel}
                            </span>
                            <div
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    height: 32,
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                }}
                            >
                                {month.segments.map((seg) => (
                                    <div
                                        key={seg.channelId}
                                        title={`${seg.channelId}: ${channelMixMode === 'revenue' ? formatCurrency(seg.value) : formatCount(seg.value) + ' units'} (${(seg.share * 100).toFixed(1)}%)`}
                                        style={{
                                            width: `${seg.share * 100}%`,
                                            backgroundColor:
                                                channelColorMap.get(
                                                    seg.channelId,
                                                ) ?? '#94a3b8',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            cursor: 'default',
                                            transition: 'opacity 0.15s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity =
                                                '0.85';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '1';
                                        }}
                                    >
                                        {seg.share >= 0.1 && (
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: '#fff',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    padding: '0 6px',
                                                }}
                                            >
                                                {seg.channelId}{' '}
                                                {Math.round(seg.share * 100)}%
                                            </span>
                                        )}
                                        {seg.share >= 0.05 &&
                                            seg.share < 0.1 && (
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        color: '#fff',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {Math.round(
                                                        seg.share * 100,
                                                    )}
                                                    %
                                                </span>
                                            )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Legend */}
                {channelMixTrendData.length > 0 && (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 12,
                            marginTop: 16,
                            paddingTop: 12,
                            borderTop: '1px solid rgba(0,0,0,0.06)',
                        }}
                    >
                        {Array.from(channelColorMap.entries()).map(
                            ([channel, color]) => (
                                <div
                                    key={channel}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontSize: 12,
                                        color: 'rgb(71, 85, 105)',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 2,
                                            backgroundColor: color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {channel}
                                </div>
                            ),
                        )}
                    </div>
                )}
            </div>

            {/* Performance — SKU and Channel as two sheets of one card. They
                share a date range, filters and period toggle, so the wrapper owns
                the toolbar and each sheet renders bare inside it. */}
            <div style={{ marginBottom: 24 }}>
                <DataSheet<SkuRow | ChannelRow>
                    data={[]}
                    columns={[]}
                    tabs={CARD_TABS}
                    activeTab={cardTab}
                    onTabChange={(tabId) => {
                        setCardTab(tabId as CardTab);
                        setSkuPage(1);
                    }}
                    title={isSkuTab ? 'SKU Performance' : 'Channel Performance'}
                    isLoading={loadingCard}
                    enableSearch
                    searchPlaceholder={
                        isSkuTab ? 'Search SKU...' : 'Search channel...'
                    }
                    searchValue={cardSearch}
                    onSearchChange={setCardSearch}
                    enableDownload
                    allColumns={
                        (showAsSold
                            ? asSoldColumns
                            : isSkuTab
                              ? skuDownloadColumns
                              : channelDownloadColumns) as never
                    }
                    onFetchAllData={
                        (showAsSold
                            ? async () => ({ rows: asSoldData })
                            : isSkuTab
                              ? async () => ({ rows: skuDisplayData })
                              : async () => ({
                                    rows: channelTableData,
                                })) as never
                    }
                    downloadFilename={
                        showAsSold
                            ? `sku-performance-as-sold-${skuMode}.csv`
                            : isSkuTab
                              ? `sku-performance-${cardTrendPeriod}-${skuMode}.csv`
                              : `channel-performance-${cardTrendPeriod}.csv`
                    }
                    filters={[cardChannelFilterConfig, cardProductFilterConfig]}
                    toolbarControls={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            {isSkuTab && showBundles && (
                                <SegmentedToggle
                                    value={bundleView}
                                    onChange={(value) => {
                                        setBundleViewChoice(value as BundleView);
                                        setSkuPage(1);
                                    }}
                                    options={[
                                        { label: 'By product', value: 'byProduct' },
                                        { label: 'As sold', value: 'asSold' },
                                    ]}
                                />
                            )}
                            {isSkuTab && (
                                <SegmentedToggle
                                    value={skuMode}
                                    onChange={setSkuMode}
                                    options={[
                                        { label: 'Top Selling', value: 'top' },
                                        { label: 'Slow Moving', value: 'slow' },
                                    ]}
                                />
                            )}
                            <PeriodControls
                                period={cardTrendPeriod}
                                onPeriodChange={setCardTrendPeriod}
                                range={cardRange}
                                onRangeChange={setCardRangeOverride}
                                onRangeReset={
                                    cardRangeOverride
                                        ? () => setCardRangeOverride(null)
                                        : undefined
                                }
                            />
                            {cardTrendPeriod === 'summary' && !showAsSold && (
                                <ColumnPicker
                                    columns={
                                        isSkuTab
                                            ? SKU_HIDEABLE_COLUMNS
                                            : CHANNEL_HIDEABLE_COLUMNS
                                    }
                                    hidden={
                                        isSkuTab
                                            ? skuHiddenCols
                                            : channelHiddenCols
                                    }
                                    onChange={
                                        isSkuTab
                                            ? setSkuHiddenCols
                                            : setChannelHiddenCols
                                    }
                                />
                            )}
                        </div>
                    }
                    onAddToChat={addToChat}
                    footer={
                        isSkuTab && skuRowCount > skuPageSize ? (
                            <DataSheetPagination
                                currentPage={skuPage}
                                totalPages={skuTotalPages}
                                pageSize={skuPageSize}
                                totalItems={skuRowCount}
                                pageSizeOptions={[10, 20, 50]}
                                onPageChange={setSkuPage}
                                onPageSizeChange={(size) => {
                                    setSkuPageSize(size);
                                    setSkuPage(1);
                                }}
                            />
                        ) : undefined
                    }
                >
                    {showAsSold ? (
                        <DataSheet<AsSoldRow>
                            key="as-sold"
                            bare
                            hideHeaderBar
                            isLoading={loadingCard}
                            data={paginatedAsSoldData}
                            columns={asSoldColumns}
                            aggregateRow={asSoldAggregateRow}
                            getRowKey={(row) => row.id}
                            emptyState="No SKU data available."
                        />
                    ) : isSkuTab ? (
                        <DataSheet<SkuRow>
                            key={showBundles ? 'by-product' : 'sku'}
                            bare
                            hideHeaderBar
                            isLoading={loadingCard}
                            data={paginatedSkuData}
                            columns={
                                cardTrendPeriod === 'summary'
                                    ? skuColumns
                                    : skuPeriodColumns
                            }
                            aggregateRow={skuAggregateRow}
                            getRowKey={(row) => row.productId}
                            getSubRows={
                                showBundles
                                    ? (row) => row.bundleChildren
                                    : undefined
                            }
                            onRowClick={(row) => {
                                if (!row.isBundleChild) openSkuPane(row);
                            }}
                            emptyState="No SKU data available."
                        />
                    ) : (
                        <DataSheet<ChannelRow>
                            bare
                            hideHeaderBar
                            isLoading={loadingCard}
                            data={channelTableData}
                            columns={
                                cardTrendPeriod === 'summary'
                                    ? channelColumns
                                    : channelPeriodColumns
                            }
                            aggregateRow={channelAggregateRow}
                            getRowKey={(row) => row.channelId}
                            onRowClick={openChannelPane}
                            emptyState="No channel data available."
                        />
                    )}
                </DataSheet>
            </div>

            {/* ----------------------------------------------------------------- */}
            {/*  Channel detail pane — fixed overlay                             */}
            {/* ----------------------------------------------------------------- */}
            {selectedChannelId && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 30,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-end',
                        backgroundColor: 'rgba(15, 23, 42, 0.10)',
                        backdropFilter: 'blur(2px)',
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeChannelPane();
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            width: '80%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Pane header */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 16,
                                padding: '20px 24px',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                flexShrink: 0,
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            <h2
                                style={{
                                    margin: 0,
                                    fontSize: 18,
                                    fontWeight: 600,
                                    color: 'rgb(26, 32, 44)',
                                    lineHeight: 1.3,
                                }}
                            >
                                {selectedChannelId} — Sales Breakdown
                            </h2>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexShrink: 0,
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={handlePaneExportCsv}
                                    title="Export channel data"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 12px',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: 'rgb(100, 116, 139)',
                                        backgroundColor: 'transparent',
                                        border: '1px solid rgba(0,0,0,0.1)',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Export size={14} />
                                    Export
                                </button>
                                <button
                                    type="button"
                                    onClick={closeChannelPane}
                                    aria-label="Close channel details"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: '50%',
                                        color: 'rgb(100, 116, 139)',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <XCircle size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Pane KPI cards */}
                        <div
                            style={{
                                display: 'flex',
                                gap: 12,
                                padding: '16px 24px',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                flexShrink: 0,
                                backgroundColor: '#FAFAFA',
                            }}
                        >
                            <KpiCard
                                label={revenueColumnHeader(revenueMode)}
                                value={formatCurrency(paneChannelRevenue)}
                            />
                            <KpiCard
                                label="Units"
                                value={formatUnits(paneChannelUnits)}
                            />
                            <KpiCard
                                label="Returns"
                                value={
                                    returnsAvailable
                                        ? formatCurrency(paneChannelReturnValue)
                                        : '—'
                                }
                                helperText={
                                    returnsAvailable
                                        ? `${formatUnits(paneChannelReturnUnits)} units`
                                        : undefined
                                }
                            />
                            <KpiCard
                                label="Net Sales"
                                value={
                                    returnsAvailable
                                        ? formatCurrency(
                                              paneChannelRevenue -
                                                  paneChannelReturnValue,
                                          )
                                        : '—'
                                }
                                helperText={
                                    returnsAvailable
                                        ? `${formatUnits(paneChannelUnits - paneChannelReturnUnits)} units`
                                        : undefined
                                }
                            />
                            <KpiCard
                                label="Growth (Units)"
                                value={paneGrowthLabel}
                                trendDirection={trendDirection(paneGrowthLabel)}
                            />
                            <KpiCard
                                label="Share (Units)"
                                value={paneShareLabel}
                            />
                        </div>

                        {/* Pane body — scrollable */}
                        <div
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                padding: '20px 24px 24px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 20,
                            }}
                        >
                            {/* Pane trend chart */}
                            <GenericChartCard<SalesChartPoint>
                                kind="line"
                                title={`Units Trend (${selectedChannelId})`}
                                data={paneTrendData}
                                dimensionKey="date"
                                dimensionType="date"
                                series={[
                                    {
                                        id: 'units',
                                        key: 'units' as keyof SalesChartPoint,
                                        label: 'Units',
                                        color: '#2563eb',
                                        renderAs: 'line',
                                    },
                                ]}
                                height={220}
                                controls={{
                                    showToolbar: true,
                                    showRange: false,
                                    showSeriesToggles: false,
                                }}
                                loading={loadingPane}
                            />

                            {/* Pane breakdown table */}
                            <DataSheet<BreakdownRow>
                                data={paneBreakdownRows}
                                columns={breakdownColumns}
                                title={`Breakdown by ${GROUP_BY_OPTIONS.find((o) => o.value === paneGroupBy)?.label ?? 'Product'}`}
                                isLoading={loadingPane}
                                getRowKey={(row) => row.key}
                                emptyState="No breakdown data available."
                                toolbarControls={
                                    <FilterDropdown
                                        value={paneGroupBy}
                                        onChange={(val) =>
                                            setPaneGroupBy(val as GroupByOption)
                                        }
                                        options={GROUP_BY_OPTIONS}
                                        placeholder="Group by"
                                    />
                                }
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ----------------------------------------------------------------- */}
            {/*  SKU detail pane — fixed overlay                                 */}
            {/* ----------------------------------------------------------------- */}
            {selectedSkuId && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 30,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-end',
                        backgroundColor: 'rgba(15, 23, 42, 0.10)',
                        backdropFilter: 'blur(2px)',
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeSkuPane();
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            width: '80%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderLeft: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Pane header */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 16,
                                padding: '20px 24px',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                flexShrink: 0,
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                    minWidth: 0,
                                }}
                            >
                                <h2
                                    style={{
                                        margin: 0,
                                        fontSize: 18,
                                        fontWeight: 600,
                                        color: 'rgb(26, 32, 44)',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {selectedSkuMeta?.name ?? '—'} — Channel
                                    Breakdown
                                </h2>
                                <span
                                    style={{
                                        fontSize: 12,
                                        color: 'rgb(100, 116, 139)',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {selectedSkuMeta?.sku ?? selectedSkuId}
                                </span>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexShrink: 0,
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={handleSkuPaneExportCsv}
                                    title="Export SKU channel data"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '6px 12px',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: 'rgb(100, 116, 139)',
                                        backgroundColor: 'transparent',
                                        border: '1px solid rgba(0,0,0,0.1)',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Export size={14} />
                                    Export
                                </button>
                                <button
                                    type="button"
                                    onClick={closeSkuPane}
                                    aria-label="Close SKU details"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                        padding: 0,
                                        borderRadius: '50%',
                                        color: 'rgb(100, 116, 139)',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <XCircle size={18} />
                                </button>
                            </div>
                        </div>

                        {/* SKU pane KPI cards */}
                        <div
                            style={{
                                display: 'flex',
                                gap: 12,
                                padding: '16px 24px',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                flexShrink: 0,
                                backgroundColor: '#FAFAFA',
                            }}
                        >
                            <KpiCard
                                label={revenueColumnHeader(revenueMode)}
                                value={formatCurrency(skuPaneTotalRevenue)}
                            />
                            <KpiCard
                                label="Units"
                                value={formatUnits(skuPaneTotalUnits)}
                            />
                            <KpiCard
                                label="Returns"
                                value={
                                    returnsAvailable
                                        ? formatCurrency(skuPaneReturnValue)
                                        : '—'
                                }
                                helperText={
                                    returnsAvailable
                                        ? `${formatUnits(skuPaneReturnUnits)} units`
                                        : undefined
                                }
                            />
                            <KpiCard
                                label="Net Sales"
                                value={
                                    returnsAvailable
                                        ? formatCurrency(
                                              skuPaneTotalRevenue -
                                                  skuPaneReturnValue,
                                          )
                                        : '—'
                                }
                                helperText={
                                    returnsAvailable
                                        ? `${formatUnits(skuPaneTotalUnits - skuPaneReturnUnits)} units`
                                        : undefined
                                }
                            />
                            <KpiCard
                                label="Growth (Units)"
                                value={skuPaneGrowthLabel}
                                trendDirection={trendDirection(
                                    skuPaneGrowthLabel,
                                )}
                            />
                            <KpiCard
                                label="Share of Total (Units)"
                                value={skuPaneShareLabel}
                            />
                        </div>

                        {/* SKU pane body — scrollable */}
                        <div
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                padding: '20px 24px 24px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 20,
                            }}
                        >
                            <DataSheet<ChannelRow>
                                data={skuPaneBreakdownRows}
                                columns={channelColumns}
                                title="Sales by Channel"
                                isLoading={loadingSkuPane}
                                getRowKey={(row) => row.channelId}
                                emptyState="No channel data available for this SKU."
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesContainer;
